import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
    Target, Zap, Film, BarChart2, Clapperboard,
    Megaphone, Settings, Plus, Trash2, Edit, Copy,
    ArrowRight, ArrowLeft, Video, UploadCloud, FileText, Lightbulb, TrendingUp, Users,
    Palette, Info, Camera, Check, CheckCircle, XCircle, AlertTriangle, Briefcase, Download, Trophy, Star, Newspaper, MessageSquare, Handshake, ThumbsUp, ThumbsDown, DollarSign,
    PieChart as PieChartIcon, Sliders, MapPin, Send, MessageCircle, UserCog,
    X, LogOut, BrainCircuit, Banknote, ExternalLink, ChevronsUpDown, Share2, ChevronDown, CopyCheck as CopyCheckIcon,
    Instagram, Twitter, Facebook, RefreshCw, Youtube, Radar, Sparkles, ShieldQuestion, HelpCircle, Globe, Lock, ChevronUp, Box, PenSquare, AreaChart, ChevronsLeft, ChevronsRight, Database, WandSparkles, Calendar, Save, ImageIcon, Minimize, Maximize
} from 'lucide-react';
import { jsPDF } from 'jspdf';

// NEW: Interactive Charts
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, RadarChart, PolarGrid, PolarAngleAxis, ComposedChart } from 'recharts';


// Shared UI
import { 
    AppCard, AppButton, AppInput, AppTextarea, BlackboxLoaderIcon, 
    SectionTitle, InfoCard, StatCard, BlackboxIcon 
} from '../../components/SharedUI';

// Services
import { 
    generateDossierHealthCheckLLM, 
    generateVisualPlaceholderLLM, 
    generateTrailerPlaceholderLLM, 
    generateBlueprintSection,
    generateTalentAnalysisLLM // <--- ADD THIS
} from '../../services/aiService';

// --- REVISED & COMPLETE: Dossier Section Components ---

const useInView = (options) => {
    const ref = useRef(null);
    const [isInView, setIsInView] = useState(false);
    useEffect(() => {
        const element = ref.current;
        if (!element) return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsInView(true);
                observer.unobserve(element);
            }
        }, { threshold: 0.1, ...options });
        observer.observe(element);
        return () => {
            if (element) observer.disconnect();
        };
    }, [options]);
    return [ref, isInView];
};

const DataPlaceholder = ({ icon: Icon, title, message }) => (
    <div className="text-center text-zinc-500 py-10 bg-zinc-800/20 rounded-lg max-w-md mx-auto">
        <Icon size={40} className="mx-auto mb-4 text-zinc-600" />
        <p className="font-semibold text-zinc-400">{title}</p>
        <p className="text-sm">{message}</p>
    </div>
);

/* --- NEW: FLOATING NAVIGATION DOTS --- */
const ChapterNavigation = ({ activeSection, scrollToSection }) => {
    const chapters = [
        { id: 'intro', label: 'Title', icon: Film },
        { id: 'health', label: 'Health Check', icon: BrainCircuit },
        { id: 'talent', label: 'Talent', icon: Star },
        { id: 'audience', label: 'Audience DNA', icon: Users },
        { id: 'visuals', label: 'Visuals', icon: Palette },
        { id: 'trailer', label: 'Trailer', icon: Video },
        { id: 'press', label: 'Press Strategy', icon: Newspaper },
        { id: 'financials', label: 'Financials', icon: DollarSign },
        { id: 'contact', label: 'Contact', icon: Handshake },
    ];

    // Map the four financial sections to one dot
    const mapFinancialSection = (id) => {
        if (['budget', 'timeline', 'revenue', 'campaigns'].includes(id)) {
            return 'financials';
        }
        return id;
    };

    return (
        <div className="fixed right-6 top-1/2 -translate-y-1/2 z-40 hidden lg:flex flex-col gap-3">
            {chapters.map((chapter) => (
                <button
                    key={chapter.id}
                    onClick={() => scrollToSection(chapter.id)}
                    className="group relative flex items-center justify-end"
                >
                    {/* Tooltip Label */}
                    <span className={`absolute right-10 px-3 py-1 rounded-md text-xs font-bold transition-all duration-300 ${
                        mapFinancialSection(activeSection) === chapter.id 
                            ? 'bg-purple-600 text-white opacity-100 translate-x-0' 
                            : 'bg-zinc-800 text-zinc-400 opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0'
                    }`}>
                        {chapter.label}
                    </span>
                    
                    {/* Dot / Icon */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 border-2 ${
                        mapFinancialSection(activeSection) === chapter.id 
                            ? 'bg-purple-600 border-purple-500 text-white scale-110 shadow-[0_0_15px_rgba(147,51,234,0.5)]' 
                            : 'bg-zinc-900/80 border-zinc-700 text-zinc-500 hover:border-zinc-500 hover:text-zinc-300'
                    }`}>
                        <chapter.icon size={16} />
                    </div>
                </button>
            ))}
        </div>
    );
};


const IntroSection = React.forwardRef(({ filmData, selectedPoster, isPublicView, onShare, onExport, isExporting, isPrintEnabled, allData, setNotification, isFullscreen }, ref) => {
    const [isInView, setIsInView] = useState(false);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsInView(true);
                observer.unobserve(element);
            }
        }, { threshold: 0.1 });
        observer.observe(element);
        return () => {
            if (element) observer.disconnect();
        };
    }, [ref]);

    const backgroundPoster = selectedPoster || 'https://source.unsplash.com/random/1920x1080/?cinema,film';

    return (
        <section ref={ref} id="intro" className="h-screen relative flex items-center justify-center text-white overflow-hidden bg-zinc-950">
            <div className="absolute inset-0 bg-cover bg-center transition-transform duration-[6000ms] ease-linear" style={{ backgroundImage: `url(${backgroundPoster})`, transform: isInView ? 'scale(1.05)' : 'scale(1)' }} />
            <div className="absolute inset-0 bg-black/60"></div>
            <div className={`relative z-10 text-center max-w-4xl p-8 transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <h1 className="text-6xl md:text-7xl font-bold font-poppins tracking-tighter" style={{ transitionDelay: '200ms' }}>{filmData.title || "Your Film Title"}</h1>
                <p className={`text-lg md:text-xl text-zinc-300 max-w-2xl mx-auto mt-4 transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: '400ms' }}>"{filmData.logline || "Your compelling logline goes here."}"</p>
                
                <div className={`mt-8 flex justify-center items-center gap-x-8 gap-y-2 flex-wrap transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: '600ms' }}>
                    {filmData.stars && (
                        <div className="text-center">
                            <p className="text-sm uppercase tracking-widest text-zinc-400">Starring</p>
                            <p className="text-lg font-semibold text-white">{filmData.stars}</p>
                        </div>
                    )}
                    {filmData.director && (
                         <div className="text-center">
                            <p className="text-sm uppercase tracking-widest text-zinc-400">Directed by</p>
                            <p className="text-lg font-semibold text-white">{filmData.director}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* --- ACTION HUB --- */}
            {!isPublicView && !isFullscreen && (
                <div className="absolute bottom-8 left-8 right-8 z-20 flex items-end justify-between gap-4">
                    {/* Marketing Blueprint Generator on the left */}
                    <MarketingBlueprintGenerator allData={allData} setNotification={setNotification} isPublicView={isPublicView} />

                    {/* Export Actions on the right — Social Plan and PR Plan are the two
                        primary deliverables; Appendix (financials, greenlight, competitive
                        intel) is optional supporting material, not folded into either. */}
                    <div className="flex flex-col gap-2 items-end">
                        <div className="flex gap-2">
                            <AppButton
                                onClick={() => generateSocialPlanPDF(allData, setNotification)}
                                icon={FileText}
                                variant="primary"
                                className="bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-500 hover:to-orange-400"
                            >
                                Social Plan
                            </AppButton>
                            <AppButton
                                onClick={() => generatePRPlanPDF(allData, setNotification)}
                                icon={FileText}
                                variant="primary"
                                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500"
                            >
                                PR Plan
                            </AppButton>
                        </div>
                        <div className="flex gap-2 items-center">
                            <AppButton onClick={onShare} icon={Share2} variant="secondary" size="sm">Share</AppButton>
                            <AppButton
                                onClick={() => handleExportMasterDossier(allData, setNotification)}
                                icon={FileText}
                                variant="secondary"
                                size="sm"
                            >
                                Appendix
                            </AppButton>
                        </div>
                    </div>
                </div>
            )}

            {isPublicView && isPrintEnabled && !isFullscreen && (
                <div className="absolute bottom-8 right-8 z-20">
                     <AppButton onClick={onExport} icon={FileText} variant="secondary" disabled={isExporting}>
                        {isExporting ? 'Exporting...' : 'Export PDF'}
                    </AppButton>
                </div>
            )}
        </section>
    );
});

// --- UPGRADED: HealthCheckSection with Editing ---
// --- UPGRADED: HealthCheckSection with Manual Regenerate ---
const HealthCheckSection = ({ allData, saveFilmData, setNotification }) => {
    const [ref, isInView] = useInView();
    const [healthData, setHealthData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editedSummary, setEditedSummary] = useState('');

    useEffect(() => {
        const fetchHealthCheck = async () => {
            // Priority: Saved Data > Generate New
            if (allData?.filmData?.health_check_snapshot) {
                setHealthData(allData.filmData.health_check_snapshot);
                setEditedSummary(allData.filmData.health_check_snapshot.overallHealth);
                setIsLoading(false);
                return;
            }

            if (!allData || !allData.filmData) {
                setIsLoading(false);
                setHealthData({ overallHealth: 'Awaiting project data for analysis.', biggestStrength: 'N/A', greatestRisk: 'N/A' });
                return;
            };

            // Only auto-generate if we have NO data at all
            setIsLoading(true);
            try {
                const result = await generateDossierHealthCheckLLM(allData);
                setHealthData(result);
                setEditedSummary(result.overallHealth);
                
                if (saveFilmData && allData.filmData) {
                    await saveFilmData({ ...allData.filmData, health_check_snapshot: result });
                }
            } catch (error) {
                // Don't save the error message to DB, just show locally
                setHealthData({ overallHealth: 'Could not generate AI analysis.', biggestStrength: 'N/A', greatestRisk: 'N/A' });
            } finally {
                setIsLoading(false);
            }
        };
        fetchHealthCheck();
    }, [allData, saveFilmData]);

    // NEW: Manual Trigger Function
    const handleRegenerate = async () => {
        setIsLoading(true);
        setNotification({ type: 'info', message: 'Regenerating Health Check...' });
        try {
            // Force new generation
            const result = await generateDossierHealthCheckLLM(allData);
            
            setHealthData(result);
            setEditedSummary(result.overallHealth);
            
            // Overwrite existing data in Firebase (fixes the stuck error message)
            if (saveFilmData && allData.filmData) {
                await saveFilmData({ ...allData.filmData, health_check_snapshot: result });
            }
            setNotification({ type: 'success', message: 'Health Check refreshed!' });
        } catch (error) {
            console.error("Regeneration Failed:", error);
            setNotification({ type: 'error', message: 'Regeneration failed. Check console.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        const updatedData = { ...healthData, overallHealth: editedSummary };
        setHealthData(updatedData);
        if (saveFilmData && allData.filmData) {
            await saveFilmData({ ...allData.filmData, health_check_snapshot: updatedData });
        }
        setIsEditing(false);
        setNotification({ type: 'success', message: 'Health Check updated.' });
    };

    return (
        <section id="health" ref={ref} className="bg-zinc-900 p-8 md:p-16 flex items-center justify-center min-h-screen">
            <div className="max-w-6xl w-full">
                <div className={`transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="flex justify-center items-center gap-4 mb-8">
                        <h2 className="text-5xl font-semibold font-poppins text-white">mi Health Check</h2>
                        {/* REGENERATE BUTTON */}
                        {!isLoading && (
                            <AppButton 
                                onClick={handleRegenerate} 
                                variant="ghost" 
                                size="sm" 
                                className="rounded-full p-2 hover:bg-zinc-800 text-zinc-500 hover:text-white transition-colors"
                                title="Regenerate Analysis"
                            >
                                <RefreshCw size={20} />
                            </AppButton>
                        )}
                    </div>
                </div>
                {isLoading ? (
                    <div className="flex justify-center flex-col items-center gap-4">
                        <BlackboxLoaderIcon className="w-12 h-12" />
                        <p className="text-zinc-500 text-sm animate-pulse">Analyzing project data...</p>
                    </div>
                ) : (
                    <div className={`transition-opacity duration-500 ${healthData ? 'opacity-100' : 'opacity-0'}`}>
                        <AppCard className="p-6 bg-zinc-800/50 border-l-4 border-purple-500 mb-8 relative group">
                            {isEditing ? (
                                <div className="space-y-4">
                                    <AppTextarea 
                                        value={editedSummary} 
                                        onChange={(e) => setEditedSummary(e.target.value)} 
                                        rows={3} 
                                        className="w-full text-lg"
                                    />
                                    <div className="flex justify-end gap-2">
                                        <AppButton size="sm" variant="secondary" onClick={() => { setIsEditing(false); setEditedSummary(healthData.overallHealth); }}>Cancel</AppButton>
                                        <AppButton size="sm" onClick={handleSave}>Save Narrative</AppButton>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <p className="text-lg text-zinc-300">"{healthData.overallHealth}"</p>
                                    <button 
                                        onClick={() => setIsEditing(true)} 
                                        className="absolute top-4 right-4 text-zinc-500 hover:text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <Edit size={16} />
                                    </button>
                                </>
                            )}
                        </AppCard>
                        <div className="grid md:grid-cols-2 gap-8">
                            <InfoCard icon={TrendingUp} title="Biggest Strength" className="bg-zinc-800/30 border-green-500 border-l-4">
                                <p className="text-zinc-300">{healthData.biggestStrength}</p>
                            </InfoCard>
                            <InfoCard icon={AlertTriangle} title="Greatest Risk" className="bg-zinc-800/30 border-red-500 border-l-4">
                                <p className="text-zinc-300">{healthData.greatestRisk}</p>
                            </InfoCard>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

// --- UPGRADED: Talent Strategy Section (Real Data) ---
const TalentStrategySection = ({ filmData, saveFilmData, setNotification, credits, deductCredit }) => {
    const [ref, isInView] = useInView();
    const [isLoading, setIsLoading] = useState(false);
    
    // Use saved analysis or fallback to null
    const analysis = filmData?.talent_analysis || null;

    const handleAnalyzeCast = async () => {
        if (!filmData.stars) {
            setNotification({ type: 'error', message: 'Please add cast members in the "Title" section first.' });
            return;
        }
        
        // --- GATEKEEPER ---
        // You might want to pass 'credits' prop down to DossierTab if you want to enforce costs here
        // For now, we'll assume standard processing
        
        setIsLoading(true);
        try {
            // Import this function at the top of DossierTab.js
            const result = await generateTalentAnalysisLLM(filmData);
            
            await saveFilmData({ ...filmData, talent_analysis: result });
            setNotification({ type: 'success', message: 'Cast marketability analyzed!' });
        } catch (error) {
            console.error(error);
            setNotification({ type: 'error', message: 'Analysis failed.' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <section id="talent" ref={ref} className="min-h-screen bg-zinc-900 p-8 md:p-16 flex items-center">
            <div className="w-full max-w-6xl mx-auto">
                <div className={`transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-5xl font-semibold font-poppins mb-4 text-center text-pink-500">Talent & Marketability</h2>
                    <p className="text-center text-zinc-400 mb-12">Cast Valuation & Audience Draw</p>
                </div>

                {!analysis ? (
                    // --- EMPTY STATE: "Analyze" Button ---
                    <div className="text-center py-12 bg-zinc-800/30 rounded-2xl border border-zinc-700">
                        <Users size={48} className="mx-auto text-zinc-600 mb-4" />
                        <h3 className="text-xl font-bold text-white mb-2">Analyze Your Cast</h3>
                        <p className="text-zinc-400 max-w-md mx-auto mb-6">
                            Generate a marketability report for <strong>{filmData.stars || "your cast"}</strong>. 
                            mi will estimate social reach, audience demographics, and synergy.
                        </p>
                        <AppButton 
                            onClick={handleAnalyzeCast} 
                            disabled={isLoading || !filmData.stars} 
                            size="lg" 
                            icon={Sparkles}
                            className="bg-pink-600 hover:bg-pink-500"
                        >
                            {isLoading ? 'Analyzing Market Value...' : 'Run Cast Analysis'}
                        </AppButton>
                    </div>
                ) : (
                    // --- REAL DATA DISPLAY ---
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        
                        {/* LEFT: CAST CARDS */}
                        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                            {analysis.cast_members.map((actor, idx) => (
                                <AppCard key={idx} className={`p-0 overflow-hidden flex flex-col transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`} style={{transitionDelay: `${200 + idx * 150}ms`}}>
                                    <div className="h-48 bg-zinc-800 relative">
                                        {/* Dynamic Gradient based on index */}
                                        <div className={`absolute inset-0 bg-gradient-to-br ${idx % 2 === 0 ? 'from-pink-900/50 to-zinc-900' : 'from-purple-900/50 to-zinc-900'}`} />
                                        
                                        {/* Optional: Add real image search here later if you integrate TMDB API */}
                                        
                                        <div className="absolute bottom-4 left-4">
                                            <h3 className="text-2xl font-bold text-white font-poppins">{actor.name}</h3>
                                            <p className="text-sm text-zinc-300">{actor.role}</p>
                                        </div>
                                    </div>
                                    <div className="p-6 space-y-4 bg-zinc-900/50 flex-grow">
                                        <div>
                                            <p className="text-xs font-bold text-zinc-500 uppercase mb-1">Known For</p>
                                            <div className="flex flex-wrap gap-2">
                                                {actor.knownFor.map(k => (
                                                    <span key={k} className="px-2 py-1 bg-zinc-800 rounded text-xs text-white border border-zinc-700">{k}</span>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-end border-t border-zinc-800 pt-4">
                                            <div>
                                                <p className="text-xs font-bold text-zinc-500 uppercase">Primary Draw</p>
                                                <p className="text-sm text-pink-400 font-medium">{actor.demographic}</p>
                                            </div>
                                            <div className="text-right">
                                                <div className="flex items-center gap-1 text-zinc-400 justify-end">
                                                    <Instagram size={14} />
                                                    <span className="text-xs">Est. Reach</span>
                                                </div>
                                                <p className="text-xl font-bold text-white">{actor.socialReach}</p>
                                            </div>
                                        </div>
                                    </div>
                                </AppCard>
                            ))}
                        </div>

                        {/* RIGHT: STRATEGY ANALYSIS */}
                        <div className={`space-y-6 transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`} style={{transitionDelay: '600ms'}}>
                            
                            {/* TOTAL REACH CARD */}
                            <div className="bg-gradient-to-r from-pink-900/20 to-purple-900/20 border border-pink-500/30 p-6 rounded-2xl text-center">
                                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-pink-500/20 text-pink-400 mb-3">
                                    <Users size={24} />
                                </div>
                                <h4 className="text-3xl font-bold text-white mb-1">{analysis.combined_reach}</h4>
                                <p className="text-sm text-zinc-400 uppercase tracking-widest">Combined Social Reach</p>
                            </div>

                            {/* AI ANALYSIS CARD */}
                            <InfoCard icon={Sparkles} title="Casting Synergy" className="bg-zinc-800/30 h-auto">
                                <div className="space-y-4">
                                    <div>
                                        <strong className="text-zinc-200 block mb-1">{analysis.casting_synergy?.title}</strong>
                                        <p className="text-sm text-zinc-400 leading-relaxed">
                                            {analysis.casting_synergy?.description}
                                        </p>
                                    </div>
                                    <div>
                                        <strong className="text-zinc-200 block mb-1">Marketability Score</strong>
                                        <div className="w-full bg-zinc-900 rounded-full h-2.5 mt-2">
                                            <div 
                                                className="bg-gradient-to-r from-pink-500 to-purple-500 h-2.5 rounded-full transition-all duration-1000" 
                                                style={{ width: `${(analysis.marketability_score || 5) * 10}%` }}
                                            ></div>
                                        </div>
                                        <p className="text-right text-xs text-pink-400 mt-1 font-mono">{analysis.marketability_score}/10</p>
                                    </div>
                                </div>
                            </InfoCard>

                            {/* RE-RUN BUTTON (Subtle) */}
                            <button 
                                onClick={handleAnalyzeCast} 
                                className="w-full text-xs text-zinc-600 hover:text-zinc-400 mt-4 flex items-center justify-center gap-1"
                            >
                                <WandSparkles size={12} /> Refresh Analysis
                            </button>

                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};
    
const AudienceDnaSection = ({ filmData }) => {
    const [ref, isInView] = useInView();
    const audienceAnalysis = filmData?.ai_analyses?.[filmData.ai_analyses.length - 1]?.analysis;
    const { target_audience_breakdown, marketing_pillars_strategy, top_us_theatrical_markets } = audienceAnalysis || {};

    // Helper for animated items
    const getDelay = (index) => ({ transitionDelay: `${200 + index * 100}ms` });

    const StatPill = ({ children, index, inView }) => (
        <div 
            className={`bg-zinc-800 text-zinc-200 py-2 px-4 rounded-full transition-all duration-500 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} 
            style={getDelay(index)}
        >
            {children}
        </div>
    );

    return (
        <section id="audience" ref={ref} className="min-h-screen bg-zinc-950 p-8 md:p-16 flex items-center">
            <div className="w-full max-w-7xl mx-auto">
                <div className={`transition-all duration-1000 ease-out ${isInView ? 'opacity-100' : 'opacity-0 -translate-x-10'}`}>
                    <h2 className="text-5xl font-semibold font-poppins mb-12 text-purple-400 text-center">Audience DNA</h2>
                    {audienceAnalysis ? (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            
                            {/* --- Column 1: Target Profiles --- */}
                            <div className="space-y-8">
                                <InfoCard icon={Users} title="Target Profiles" className="bg-zinc-800/30 h-full">
                                    {/* THIS IS THE FIX: The content inside this card is restructured for tighter spacing. */}
                                    <div className="space-y-2">
                                        <p className="text-sm font-semibold text-zinc-400">Primary Audience</p>
                                        <p className="font-bold text-white text-xl">{target_audience_breakdown?.primary_audience_profile}</p>
                                    </div>
                                    <div className="border-t border-zinc-700 my-4"></div>
                                    <div className="space-y-2">
                                        <p className="text-sm font-semibold text-zinc-400">Secondary Audience</p>
                                        <p className="text-zinc-300 text-lg">{target_audience_breakdown?.secondary_audience_profile}</p>
                                    </div>
                                </InfoCard>
                            </div>

                            {/* --- Column 2: Core Pillars --- */}
                            <div className="space-y-8">
                                <InfoCard icon={Target} title="Core Marketing Pillars" className="bg-zinc-800/30 border-purple-400/50 h-full">
                                    <div className="space-y-5">
                                        {marketing_pillars_strategy?.pillars?.map((pillar, index) => (
                                            <div key={index} className={`transition-all duration-500 ease-out ${isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-5'}`} style={getDelay(index)}>
                                                <h4 className="font-bold text-white text-lg">{pillar.pillar}</h4>
                                                <p className="text-zinc-400 text-sm">{pillar.detail}</p>
                                            </div>
                                        ))}
                                    </div>
                                </InfoCard>
                            </div>

                            {/* --- Column 3: Supporting Data --- */}
                            <div className="space-y-8">
                                <InfoCard icon={Lightbulb} title="Psychographics & Interests" className="bg-zinc-800/30">
                                    <div className="flex flex-wrap gap-3">
                                        {target_audience_breakdown?.psychographics?.map((trait, index) => (
                                            <StatPill key={trait} index={index} inView={isInView}>{trait}</StatPill>
                                        ))}
                                    </div>
                                </InfoCard>
                                <InfoCard icon={Film} title="Comparable Films" className="bg-zinc-800/30">
                                     <div className="flex flex-wrap gap-3">
                                      {target_audience_breakdown?.comparable_films?.map((film, index) => (
                                            <StatPill key={film} index={index} inView={isInView}>{film}</StatPill>
                                        ))}
                                    </div>
                                </InfoCard>
                                <InfoCard icon={MapPin} title="Top 5 Theatrical Markets" className="bg-zinc-800/30">
                                    <ol className="list-decimal list-inside space-y-2 font-semibold text-zinc-300">
                                        {top_us_theatrical_markets?.slice(0, 5).map(market => <li key={market}>{market}</li>)}
                                    </ol>
                                </InfoCard>
                            </div>
                        </div>
                    ) : (
                        <DataPlaceholder icon={Target} title="No Audience Analysis" message="Run 'Audience Genius' to generate this data." />
                    )}
                </div>
            </div>
        </section>
    );
};

const VisualStrategySection = ({ visualAnalysis, posterA, posterB, filmData = {}, saveFilmData, setNotification }) => {
    const [ref, isInView] = useInView();
    const [aiPlaceholder, setAiPlaceholder] = useState('');
    const [isLoadingPlaceholder, setIsLoadingPlaceholder] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editableContent, setEditableContent] = useState('');

    // Determine the primary visual: official poster or a provisional ad concept
    const provisionalVisual = useMemo(() => {
        if (filmData?.ad_concepts && filmData.ad_concepts.length > 0) {
            return filmData.ad_concepts[0].ad_a_img || filmData.ad_concepts[0].ad_b_img;
        }
        return null;
    }, [filmData]);

    const winningPoster = visualAnalysis?.recommendation === 'Version A' ? posterA : posterB;
    const displayVisual = winningPoster || provisionalVisual;
    const hasOfficialArt = !!winningPoster;

    // Initialize content from saved data or generate new
    useEffect(() => {
        if (filmData?.visual_identity_placeholder) {
            setAiPlaceholder(filmData.visual_identity_placeholder);
            setEditableContent(filmData.visual_identity_placeholder);
        }
    }, [filmData?.visual_identity_placeholder]);

    const fetchPlaceholder = useCallback(async () => {
        setIsLoadingPlaceholder(true);
        try {
            const placeholderText = await generateVisualPlaceholderLLM(filmData);
            setAiPlaceholder(placeholderText);
            setEditableContent(placeholderText);
            
            // Save to project data
            if (saveFilmData && filmData) {
                await saveFilmData({ 
                    ...filmData, 
                    visual_identity_placeholder: placeholderText 
                });
            }
        } catch (error) {
            console.error("Failed to generate visual placeholder:", error);
            const fallbackText = "The visual identity for this film is currently in development to perfectly capture its unique tone and narrative.";
            setAiPlaceholder(fallbackText);
            setEditableContent(fallbackText);
        }
        setIsLoadingPlaceholder(false);
    }, [filmData, saveFilmData]);

    useEffect(() => {
        if (isInView && !displayVisual && !aiPlaceholder && !isLoadingPlaceholder && !filmData?.visual_identity_placeholder) {
            fetchPlaceholder();
        }
    }, [isInView, displayVisual, aiPlaceholder, isLoadingPlaceholder, fetchPlaceholder, filmData?.visual_identity_placeholder]);

    const handleRegenerate = () => {
        setAiPlaceholder('');
        setEditableContent('');
        fetchPlaceholder();
    };

    const handleSaveEdit = async () => {
        if (saveFilmData && filmData) {
            try {
                await saveFilmData({ 
                    ...filmData, 
                    visual_identity_placeholder: editableContent 
                });
                setAiPlaceholder(editableContent);
                setIsEditing(false);
                if (setNotification) {
                    setNotification({ type: 'success', message: 'Visual identity content updated!' });
                }
            } catch (error) {
                if (setNotification) {
                    setNotification({ type: 'error', message: 'Failed to save changes.' });
                }
            }
        }
    };

    const handleCancelEdit = () => {
        setEditableContent(aiPlaceholder);
        setIsEditing(false);
    };

    return (
        <section id="visuals" ref={ref} className="min-h-screen bg-zinc-900 p-8 md:p-16 flex items-center">
            <div className="w-full max-w-6xl mx-auto">
                <div className={`transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                     <h2 className="text-5xl font-semibold font-poppins mb-8 text-cyan-400 text-center">Visual Identity</h2>
                </div>
                
                {displayVisual ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className={`transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`} style={{transitionDelay: '200ms'}}>
                            <div className="relative">
                                <img src={displayVisual} alt="Film Visual" className="rounded-lg w-full object-contain shadow-2xl border-2 border-cyan-400" />
                                {!hasOfficialArt && (
                                    <div className="absolute -top-4 -left-4 bg-cyan-400 text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                                        Provisional Concept Art
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={`space-y-6 transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`} style={{transitionDelay: '400ms'}}>
                            {visualAnalysis ? (
                                <>
                                    <h3 className="text-3xl font-semibold font-poppins flex items-center gap-3 text-white"><Palette className="text-cyan-400"/> mi Poster Analysis</h3>
                                    <div>
                                        <p className="text-sm uppercase tracking-widest text-zinc-400">mi Recommendation</p>
                                        <p className="text-2xl font-bold text-white">{visualAnalysis.recommendation}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm uppercase tracking-widest text-zinc-400">Key Insight</p>
                                        <p className="text-md text-zinc-300">{visualAnalysis.insight}</p>
                                    </div>
                                </>
                            ) : (
                                <InfoCard icon={Lightbulb} title="Creative Direction" className="bg-zinc-800/30">
                                    <p className="text-zinc-300">The poster is in development. The visual here represents an initial concept direction from our AI Ad Studio, designed to capture the core thematic elements of the film and engage the primary target audience.</p>
                                </InfoCard>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className={`transition-all duration-1000 ease-out ${isInView ? 'opacity-100' : 'opacity-0'}`}>
                        {isLoadingPlaceholder ? (
                            <div className="flex justify-center items-center h-64"><BlackboxLoaderIcon className="w-12 h-12" /></div>
                        ) : (
                            <InfoCard 
                                icon={Palette} 
                                title={
                                    <div className="flex justify-between items-center w-full">
                                        <span>Visual Identity Vision</span>
                                        <div className="flex gap-2">
                                            {aiPlaceholder && !isEditing && (
                                                <AppButton 
                                                    onClick={() => setIsEditing(true)} 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    className="!p-1.5" 
                                                    icon={PenSquare} 
                                                    title="Edit Content"
                                                />
                                            )}
                                            <AppButton 
                                                onClick={handleRegenerate} 
                                                variant="ghost" 
                                                size="sm" 
                                                className="!p-1.5" 
                                                icon={WandSparkles} 
                                                title="Regenerate with mi"
                                            />
                                        </div>
                                    </div>
                                } 
                                className="max-w-3xl mx-auto bg-zinc-800/50 border-cyan-400/50"
                            >
                                {isEditing ? (
                                    <div className="space-y-4">
                                        <AppTextarea
                                            value={editableContent}
                                            onChange={(e) => setEditableContent(e.target.value)}
                                            rows={4}
                                            className="w-full"
                                            placeholder="Enter your visual identity description..."
                                        />
                                        <div className="flex justify-end gap-2">
                                            <AppButton onClick={handleCancelEdit} variant="secondary" size="sm">
                                                Cancel
                                            </AppButton>
                                            <AppButton onClick={handleSaveEdit} size="sm">
                                                Save Changes
                                            </AppButton>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-zinc-300 text-lg leading-relaxed">{aiPlaceholder}</p>
                                )}
                            </InfoCard>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};

const TrailerDnaSection = ({ videoAnalysis, videoKeyframes, filmData, saveFilmData, setNotification, isPublicView = false }) => {
  const [ref, isInView] = useInView();
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [imageErrors, setImageErrors] = useState(new Set());
  const [aiPlaceholder, setAiPlaceholder] = useState('');
  const [isLoadingPlaceholder, setIsLoadingPlaceholder] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editableContent, setEditableContent] = useState('');
  
  const handleImageError = (index) => {
    setImageErrors(prev => new Set([...prev, index]));
  };

  const validKeyframes = videoKeyframes?.filter((_, index) => !imageErrors.has(index)) || [];
  
  useEffect(() => {
    if (validKeyframes.length > 0 && !selectedFrame) {
      let bestFrameIndex = Math.min(3, validKeyframes.length - 1);
      if (bestFrameIndex >= 0) {
        setSelectedFrame(validKeyframes[bestFrameIndex]);
      }
    }
  }, [validKeyframes, selectedFrame]);

  // Initialize content from saved data
  useEffect(() => {
    if (filmData?.trailer_strategy_placeholder) {
      setAiPlaceholder(filmData.trailer_strategy_placeholder);
      setEditableContent(filmData.trailer_strategy_placeholder);
    }
  }, [filmData?.trailer_strategy_placeholder]);

  const fetchPlaceholder = useCallback(async () => {
    setIsLoadingPlaceholder(true);
    try {
        const placeholderText = await generateTrailerPlaceholderLLM(filmData);
        setAiPlaceholder(placeholderText);
        setEditableContent(placeholderText);
        
        // Save to project data
        if (saveFilmData && filmData) {
            await saveFilmData({ 
                ...filmData, 
                trailer_strategy_placeholder: placeholderText 
            });
        }
    } catch (error) {
        console.error("Failed to generate trailer placeholder:", error);
        const fallbackText = "The official trailer is in post-production. The editorial direction will focus on building suspense and highlighting the core emotional conflict, tailored to resonate strongly with the film's target demographic.";
        setAiPlaceholder(fallbackText);
        setEditableContent(fallbackText);
    }
    setIsLoadingPlaceholder(false);
  }, [filmData, saveFilmData]);

  useEffect(() => {
      if (isInView && !videoAnalysis && !aiPlaceholder && !isLoadingPlaceholder && !filmData?.trailer_strategy_placeholder) {
          fetchPlaceholder();
      }
  }, [isInView, videoAnalysis, aiPlaceholder, isLoadingPlaceholder, fetchPlaceholder, filmData?.trailer_strategy_placeholder]);

  const handleRegenerate = () => {
      setAiPlaceholder('');
      setEditableContent('');
      fetchPlaceholder();
  };

  const handleSaveEdit = async () => {
    if (saveFilmData && filmData) {
        try {
            await saveFilmData({ 
                ...filmData, 
                trailer_strategy_placeholder: editableContent 
            });
            setAiPlaceholder(editableContent);
            setIsEditing(false);
            if (setNotification) {
                setNotification({ type: 'success', message: 'Trailer strategy content updated!' });
            }
        } catch (error) {
            if (setNotification) {
                setNotification({ type: 'error', message: 'Failed to save changes.' });
            }
        }
    }
  };

  const handleCancelEdit = () => {
    setEditableContent(aiPlaceholder);
    setIsEditing(false);
  };
  
  return (
    <section id="trailer" ref={ref} className="min-h-screen bg-zinc-950 p-8 md:p-16 flex items-center">
      <div className="w-full max-w-6xl mx-auto">
        <div className={`transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-5xl font-semibold font-poppins mb-8 text-rose-400 text-center">Trailer DNA</h2>
        </div>
        
        {videoAnalysis ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
             <div className={`transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`} style={{transitionDelay: '200ms'}}>
                {validKeyframes.length > 0 ? (
                    <div className="space-y-4">
                        <img src={selectedFrame || validKeyframes[0]} alt="Featured keyframe" className="w-full aspect-video object-cover rounded-2xl border-2 border-rose-400 shadow-2xl"/>
                        <div className="grid grid-cols-5 gap-2">
                            {validKeyframes.slice(0, 10).map((frame, index) => (
                                <button key={index} onClick={() => setSelectedFrame(frame)} className={`aspect-video rounded-lg overflow-hidden border-2 transition-all hover:scale-105 ${ (selectedFrame || validKeyframes[0]) === frame ? 'border-rose-400 ring-2 ring-rose-400/50' : 'border-zinc-700' }`} >
                                    <img src={frame} alt={`Frame ${index + 1}`} className="w-full h-full object-cover" onError={() => handleImageError(index)} />
                                </button>
                            ))}
                        </div>
                    </div>
                ) : <div className="bg-zinc-800/50 p-8 rounded-2xl border border-zinc-700 text-center"><Video className="w-16 h-16 text-zinc-600 mx-auto mb-4" /><p className="text-zinc-400">No keyframes available</p></div>}
            </div>
            <div className={`space-y-6 transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`} style={{transitionDelay: '400ms'}}>
              <InfoCard icon={FileText} title="Overall Impression" className="bg-zinc-800/30"><p className="text-zinc-300">{videoAnalysis.OverallImpression}</p></InfoCard>
              <InfoCard icon={TrendingUp} title="Predicted Buzz" className="bg-zinc-800/30"><p className="font-bold text-2xl text-rose-300">{videoAnalysis.PredictedBuzzFactor}</p></InfoCard>
              <InfoCard icon={Star} title="The 'Money Shot'" className="bg-zinc-800/30"><p className="text-zinc-300 italic">"{videoAnalysis.MoneyShot}"</p></InfoCard>
            </div>
          </div>
        ) : (
          <div className={`transition-all duration-1000 ease-out ${isInView ? 'opacity-100' : 'opacity-0'}`}>
            {isLoadingPlaceholder ? (
                <div className="flex justify-center items-center h-64"><BlackboxLoaderIcon className="w-12 h-12" /></div>
            ) : (
                <InfoCard 
                    icon={Film} 
                    title={
                        <div className="flex justify-between items-center w-full">
                            <span>Trailer Creative Strategy</span>
                            {/* FIX: Conditionally render buttons based on isPublicView */}
                            {!isPublicView && (
                                <div className="flex gap-2">
                                    {aiPlaceholder && !isEditing && (
                                        <AppButton 
                                            onClick={() => setIsEditing(true)} 
                                            variant="ghost" 
                                            size="sm" 
                                            className="!p-1.5" 
                                            icon={PenSquare} 
                                            title="Edit Content"
                                        />
                                    )}
                                    <AppButton 
                                        onClick={handleRegenerate} 
                                        variant="ghost" 
                                        size="sm" 
                                        className="!p-1.5" 
                                        icon={WandSparkles} 
                                        title="Regenerate with mi"
                                    />
                                </div>
                            )}
                        </div>
                    } 
                    className="max-w-3xl mx-auto bg-zinc-800/50 border-rose-400/50"
                >
                    {isEditing ? (
                        <div className="space-y-4">
                            <AppTextarea
                                value={editableContent}
                                onChange={(e) => setEditableContent(e.target.value)}
                                rows={4}
                                className="w-full"
                                placeholder="Enter your trailer strategy description..."
                            />
                            <div className="flex justify-end gap-2">
                                <AppButton onClick={handleCancelEdit} variant="secondary" size="sm">
                                    Cancel
                                </AppButton>
                                <AppButton onClick={handleSaveEdit} size="sm">
                                    Save Changes
                                </AppButton>
                            </div>
                        </div>
                    ) : (
                        <p className="text-zinc-300 text-lg leading-relaxed">{aiPlaceholder}</p>
                    )}
                </InfoCard>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

const PhasedTimelineSection = ({ budgetPlan }) => {
    const [ref, isInView] = useInView();
    const [expandedPhase, setExpandedPhase] = useState(null);
    return (
        <section id="timeline" ref={ref} className="bg-black p-8 md:p-16 text-white min-h-screen flex flex-col justify-center">
            <h2 className={`text-4xl font-semibold font-poppins mb-12 text-center text-sky-400 transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>Phased Marketing Rollout</h2>
            {budgetPlan?.phased_timeline ? (
                <div className="flex flex-col md:flex-row justify-between items-stretch gap-4">
                    {budgetPlan.phased_timeline.map((phase, index) => (
                        <AppCard
                            key={index}
                            className={`p-6 flex-1 cursor-pointer transition-all duration-500 ease-out ${isInView ? 'opacity-100' : 'opacity-0' } ${expandedPhase === index ? 'bg-zinc-700/50 border-purple-500 scale-105' : 'bg-zinc-800/50 border-zinc-700 hover:border-purple-400'}`}
                            style={{ transitionDelay: `${200 + index * 150}ms` }}
                            onClick={() => setExpandedPhase(expandedPhase === index ? null : index)}
                        >
                            <h3 className="text-2xl font-bold mb-3 text-white flex items-center gap-2">
                                <Calendar size={24} className="text-sky-400" /> {phase.phase}
                                {expandedPhase === index ? <ChevronUp size={20} className="ml-auto" /> : <ChevronDown size={20} className="ml-auto" />}
                            </h3>
                            {expandedPhase === index && (
                                <div className="mt-4 space-y-2 text-zinc-300 animate-fade-in">
                                    <h4 className="font-semibold text-purple-300">Key Activities:</h4>
                                    <ul className="list-disc list-inside text-sm pl-4">
                                        {phase.activities.map((activity, i) => <li key={i}>{activity}</li>)}
                                    </ul>
                                </div>
                            )}
                        </AppCard>
                    ))}
                </div>
            ) : (
                <DataPlaceholder icon={Calendar} title="No Phased Timeline" message="Generate a Budget Plan to see the project timeline." />
            )}
        </section>
    );
};

// --- UPGRADED: BudgetStrategySection with Pie Chart ---
const BudgetStrategySection = ({ budgetPlan, formatCurrency }) => {
    const [ref, isInView] = useInView();
    const totalBudget = useMemo(() => (budgetPlan?.budget_allocation_table || []).reduce((sum, item) => sum + item.dollar_amount, 0), [budgetPlan]);
    
    // Transform data for Recharts
    const chartData = useMemo(() => {
        return (budgetPlan?.budget_allocation_table || []).map(item => ({
            name: item.bucket,
            value: item.dollar_amount
        }));
    }, [budgetPlan]);

    const COLORS = ['#8b5cf6', '#ec4899', '#10b981', '#f59e0b', '#3b82f6', '#6366f1'];

    return (
        <section id="budget" ref={ref} className="min-h-screen bg-zinc-900 p-8 md:p-16 flex items-center">
             <div className="w-full max-w-6xl mx-auto">
                <div className={`transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-5xl font-semibold font-poppins mb-4 text-green-400 text-center">Financial Blueprint</h2>
                    <p className="text-center text-zinc-400 mb-12">Strategic Capital Allocation</p>
                </div>
                 {budgetPlan ? (
                     <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        {/* CHART COLUMN */}
                        <div className={`h-80 w-full transition-all duration-1000 ease-out ${isInView ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{transitionDelay: '200ms'}}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={chartData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {chartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip 
                                        contentStyle={{ backgroundColor: '#18181b', borderColor: '#27272a', borderRadius: '8px', color: '#fff' }}
                                        formatter={(value) => formatCurrency(value)}
                                    />
                                    <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ fontSize: '12px', color: '#a1a1aa' }}/>
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        {/* DATA COLUMN */}
                        <div className={`space-y-6 transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`} style={{transitionDelay: '400ms'}}>
                            <h3 className="text-3xl font-semibold font-poppins text-white">Total Marketing Budget: <span className="text-green-400">{formatCurrency(totalBudget)}</span></h3>
                            
                            <div className="bg-zinc-800/50 p-6 rounded-2xl border border-zinc-700">
                                <h4 className="font-bold text-zinc-200 mb-4 flex items-center gap-2">
                                    <Lightbulb size={20} className="text-yellow-400"/> 
                                    Strategy Highlights
                                </h4>
                                <ul className="space-y-3">
                                    {budgetPlan.executive_snapshot?.kpis.map((tip, index) => (
                                        <li key={index} className="flex items-start gap-3 text-sm text-zinc-300">
                                            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                            {tip}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                 ) : (
                    <DataPlaceholder icon={DollarSign} title="No Budget Plan" message="Generate a plan in 'Budget Planner' to see the financial strategy." />
                 )}
            </div>
        </section>
    );
};

const RevenueStrategySection = ({ revenueForecast, formatCurrency }) => {
    const [ref, isInView] = useInView();

    // Helper component for ancillary streams
    const AncillaryStreamCard = ({ title, data, colorClass }) => {
        if (!data) return null;
        // Handle V2 structure where data might be a simple number or object
        const revenue = typeof data === 'object' ? data.estimated_revenue : data;
        const rationale = typeof data === 'object' ? data.rationale : null;

        return (
            <div className={`p-4 rounded-lg border-l-4 ${colorClass} bg-zinc-800/50`}>
                <h4 className="font-semibold text-white mb-2">{title}</h4>
                <p className="text-2xl font-bold text-white mb-2">{typeof revenue === 'number' ? formatCurrency(revenue) : revenue}</p>
                {rationale && <p className="text-xs text-zinc-400">{rationale}</p>}
            </div>
        );
    };

    // Prepare Chart Data (Handles both V1 and V2 Schemas)
    const chartData = useMemo(() => {
        if (!revenueForecast) return [];

        // 🟢 V.2 SCHEMA (Scenarios: Realistic, Conservative, Breakout)
        if (revenueForecast.data?.scenarios?.realistic) {
            const realistic = revenueForecast.data.scenarios.realistic;
            return [
                { name: 'Opening Wknd', value: realistic.opening_weekend || 0, fill: '#f59e0b' },
                { name: 'Domestic', value: realistic.domestic_box_office || 0, fill: '#f97316' },
                { name: 'Intl', value: realistic.international_box_office || 0, fill: '#ef4444' },
            ];
        }

        // 🟠 V.1 SCHEMA (Legacy: Theatrical Release Projections)
        if (revenueForecast.data?.theatrical_release_projections) {
            const data = revenueForecast.data.theatrical_release_projections;
            return [
                { name: 'Opening Wknd', value: data?.opening_weekend_forecast || 0, fill: '#f59e0b' },
                { name: 'Domestic', value: data?.domestic_box_office_forecast || 0, fill: '#f97316' },
                { name: 'Intl', value: data?.international_box_office_forecast || 0, fill: '#ef4444' },
            ];
        }

        return [];
    }, [revenueForecast]);

    const isTheatrical = revenueForecast?.type === 'theatrical' || revenueForecast?.type === 'hybrid';

    return (
         <section id="revenue" ref={ref} className="min-h-screen bg-black p-8 md:p-16 flex items-center">
            <div className="w-full max-w-6xl mx-auto">
                <div className={`transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-5xl font-semibold font-poppins mb-4 text-center text-amber-400">Revenue Potential</h2>
                    <p className="text-center text-zinc-500 mb-12">Market Projections & ROI Analysis</p>
                </div>
                {!revenueForecast ? (
                    <DataPlaceholder icon={Banknote} title="No Revenue Forecast" message="Run a forecast in 'Revenue Potential' to see this data." />
                ) : isTheatrical ? (
                    <div className="space-y-12">
                         <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            
                            {/* CHART */}
                            <div className={`h-80 bg-zinc-900/50 p-6 rounded-2xl border border-zinc-800 transition-all duration-1000 ease-out ${isInView ? 'opacity-100' : 'opacity-0'}`} style={{transitionDelay: '200ms'}}>
                                <h4 className="text-sm font-bold text-zinc-400 mb-4 uppercase tracking-wider">Theatrical Performance (Realistic)</h4>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" horizontal={false} />
                                        <XAxis type="number" hide />
                                        <YAxis type="category" dataKey="name" stroke="#a1a1aa" fontSize={12} width={80} />
                                        <Tooltip 
                                            cursor={{fill: '#27272a'}}
                                            contentStyle={{ backgroundColor: '#18181b', borderColor: '#f59e0b', borderRadius: '8px', color: '#fff' }}
                                            formatter={(value) => formatCurrency(value)}
                                        />
                                        <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={30} label={{ position: 'right', fill: '#fff', fontSize: 12, formatter: (val) => formatCurrency(val) }}>
                                            {chartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.fill} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* ANALYSIS */}
                            <div className={`space-y-6 transition-all duration-1000 ease-out ${isInView ? 'opacity-100' : 'opacity-0'}`} style={{transitionDelay: '400ms'}}>
                                <InfoCard icon={TrendingUp} title="Strategic Insight" className="bg-zinc-900/50 border-amber-500/30">
                                    <div className="space-y-4">
                                        <div>
                                            <strong className="text-zinc-300 block mb-1">Underserved Opportunities</strong>
                                            <p className="text-sm text-zinc-400 leading-relaxed">
                                                {revenueForecast.data.underserved_opportunities || revenueForecast.data.scenarios?.realistic?.rationale || "Analysis pending..."}
                                            </p>
                                        </div>
                                    </div>
                                </InfoCard>
                                
                                {revenueForecast.data.competitor_analysis && (
                                    <InfoCard icon={Radar} title="Competitor Snapshot" className="bg-zinc-900/50 border-red-500/30">
                                        <div className="space-y-3">
                                            {revenueForecast.data.competitor_analysis.slice(0, 2).map((comp, i) => (
                                                <div key={i} className="p-3 bg-zinc-800/50 rounded-lg">
                                                    <p className="font-semibold text-white">{comp.title}</p>
                                                    <p className="text-xs text-zinc-400">{comp.box_office || comp.analysis}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </InfoCard>
                                )}
                            </div>
                         </div>
                         
                         {/* ANCILLARY STREAMS (Handles flattened V2 structure or nested V1) */}
                         <InfoCard icon={Sliders} title="Ancillary Revenue Streams" className="bg-zinc-900/50">
                            <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 transition-all duration-1000 ease-out ${isInView ? 'opacity-100' : 'opacity-0'}`} style={{transitionDelay: '600ms'}}>
                                <AncillaryStreamCard 
                                    title="Home Entertainment" 
                                    data={revenueForecast.data.ancillary_revenue_streams?.home_entertainment} 
                                    colorClass="border-purple-500" 
                                />
                                <AncillaryStreamCard 
                                    title="Streaming & Broadcast" 
                                    data={revenueForecast.data.ancillary_revenue_streams?.streaming_and_broadcast} 
                                    colorClass="border-sky-500" 
                                />
                                <AncillaryStreamCard 
                                    title="Merchandising & Licensing" 
                                    data={revenueForecast.data.ancillary_revenue_streams?.merchandising_and_licensing} 
                                    colorClass="border-green-500" 
                                />
                                <AncillaryStreamCard 
                                    title="Other Non-Theatrical" 
                                    data={revenueForecast.data.ancillary_revenue_streams?.other_non_theatrical} 
                                    colorClass="border-amber-500" 
                                />
                            </div>
                        </InfoCard>
                    </div>
                ) : (
                    <div className={`space-y-4 transition-all duration-1000 ease-out ${isInView ? 'opacity-100' : 'opacity-0'}`} style={{transitionDelay: '200ms'}}>
                        <InfoCard icon={TrendingUp} title="Streaming & Broadcast Forecast">
                            <div className="space-y-4">
                                <AncillaryStreamCard title="SVOD (Subscription)" data={revenueForecast.data.svod_forecast} colorClass="border-purple-500" />
                                <AncillaryStreamCard title="AVOD (Ad-Supported)" data={revenueForecast.data.avod_forecast} colorClass="border-sky-500" />
                                <AncillaryStreamCard title="Linear TV / Broadcast" data={revenueForecast.data.linear_tv_forecast} colorClass="border-amber-500" />
                            </div>
                        </InfoCard>
                    </div>
                )}
            </div>
        </section>
    );
};

const CampaignStrategySection = ({ campaigns }) => {
    const [ref, isInView] = useInView();
    const sortedCampaigns = useMemo(() => {
        if (!campaigns || campaigns.length === 0) return [];
        const statusOrder = { 'Live': 1, 'Scheduled': 2, 'Draft': 3, 'Completed': 4 };
        return [...campaigns].sort((a,b) => (statusOrder[a.status] || 5) - (statusOrder[b.status] || 5)).slice(0, 3);
    }, [campaigns]);
    return (
        <section id="campaigns" ref={ref} className="min-h-screen bg-zinc-950 p-8 md:p-16 flex items-center">
             <div className="w-full max-w-6xl mx-auto">
                <div className={`transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-5xl font-semibold font-poppins mb-8 text-orange-400 text-center">Campaign Blueprint</h2>
                </div>
                {sortedCampaigns.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {sortedCampaigns.map((campaign, index) => (
                            <AppCard key={campaign.id} className={`p-4 flex flex-col transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{transitionDelay: `${200 + index * 150}ms`}}>
                                <img src={campaign.creative?.url || `https://placehold.co/400x400/18181b/9333ea?text=${campaign.title}`} alt={campaign.title} className="w-full aspect-square object-cover rounded-lg mb-4"/>
                                <h3 className="font-bold text-lg text-white font-poppins">{campaign.title}</h3>
                                {campaign.conceptName && (
                                    <div className="mt-2 p-2 bg-zinc-800/50 rounded-md text-xs">
                                        <p className="font-semibold text-purple-400">Concept: <span className="text-zinc-300 font-normal">{campaign.conceptName}</span></p>
                                        <p className="font-semibold text-purple-400 mt-1">Hypothesis: <span className="text-zinc-300 font-normal">{campaign.hypothesis}</span></p>
                                    </div>
                                )}
                                <p className="text-sm text-zinc-400 mt-2 flex-grow">{campaign.content}</p>
                            </AppCard>
                        ))}
                    </div>
                ) : (
                    <DataPlaceholder icon={Briefcase} title="No Campaigns" message="Launch a campaign in the 'Campaign Manager' to see highlights here." />
                )}
            </div>
        </section>
    );
};

const PressStrategySection = ({ pressStrategy }) => {
    const [ref, isInView] = useInView();
    if (!pressStrategy) {
        return (
            <section id="press" ref={ref} className="min-h-screen bg-zinc-900 p-8 md:p-16 flex items-center">
                <div className="w-full max-w-6xl mx-auto">
                    <DataPlaceholder icon={Newspaper} title="No Press Strategy" message="Generate a strategy in 'Press & Outreach' to see highlights here." />
                </div>
            </section>
        );
    }

    return (
        <section id="press" ref={ref} className="min-h-screen bg-zinc-900 p-8 md:p-16 flex items-center">
            <div className="w-full max-w-6xl mx-auto">
                <div className={`transition-all duration-1000 ease-out ${isInView ? 'opacity-100' : 'opacity-0 -translate-x-10'}`}>
                    <h2 className="text-5xl font-semibold font-poppins mb-8 text-purple-400">Press & PR Strategy</h2>
                    <div className="bg-zinc-800/50 p-6 rounded-lg border border-zinc-700 mb-8">
                        <h3 className="text-2xl font-bold text-white mb-3">Executive Summary</h3>
                        <p className="text-zinc-300">{pressStrategy.executive_summary}</p>
                    </div>
                    <h3 className="text-3xl font-semibold mb-6 font-poppins text-white">Core Strategy Pillars</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {pressStrategy.strategy_pillars?.map((pillar, index) => (
                            <InfoCard key={index} icon={Target} title={pillar.pillar} className="bg-zinc-800/30">
                                <p className="text-zinc-400 text-sm">{pillar.description}</p>
                            </InfoCard>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

const generateSocialGraphicsFromPoster = (posterImage, customTagline = '', style = '1', cropPos = 'center') => {
    const graphics = [
      {
        title: "Instagram Story",
        url: posterImage,
        transform: "aspect-[9/16] object-cover",
        dimensions: { width: 1080, height: 1920 },
        tagline: customTagline,
        style: style,
        platform: "instagram-story",
        cropPosition: cropPos
      },
      {
        title: "Instagram Post", 
        url: posterImage,
        transform: "aspect-square object-cover",
        dimensions: { width: 1080, height: 1080 },
        tagline: customTagline,
        style: style,
        platform: "instagram-post",
        cropPosition: cropPos
      },
      {
        title: "Facebook Banner",
        url: posterImage, 
        transform: "aspect-[16/9] object-cover",
        dimensions: { width: 1920, height: 1080 },
        tagline: customTagline,
        style: style,
        platform: "facebook-banner",
        cropPosition: cropPos
      },
      {
        title: "Twitter Header",
        url: posterImage,
        transform: "aspect-[3/1] object-cover",
        dimensions: { width: 1500, height: 500 },
        tagline: customTagline,
        style: style,
        platform: "twitter-header",
        cropPosition: cropPos
      },
      {
        title: "YouTube Thumbnail",
        url: posterImage,
        transform: "aspect-[16/9] object-cover",
        dimensions: { width: 1920, height: 1080 },
        tagline: customTagline,
        style: style,
        platform: "youtube-thumbnail",
        cropPosition: cropPos
      },
      {
        title: "TikTok Video Cover",
        url: posterImage,
        transform: "aspect-[9/16] object-cover",
        dimensions: { width: 1080, height: 1920 },
        tagline: customTagline,
        style: style,
        platform: "tiktok-cover",
        cropPosition: cropPos
      }
    ];

    return graphics;
  };

const MarketingBlueprintGenerator = ({ allData, setNotification, isPublicView }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [progress, setProgress] = useState('');

    const hasRequiredData = allData.filmData?.ai_analyses?.length > 0 && allData.budgetPlan;

    const sectionsToGenerate = [
        { key: 'executive_summary', name: 'Executive Summary' },
        { key: 'audience_deep_dive', name: 'Target Audience Deep Dive' },
        { key: 'creative_strategy', name: 'Creative Strategy & Messaging' },
        { key: 'goto_market_plan', name: 'Go-to-Market Plan' },
        { key: 'digital_pr_activation', name: 'Digital & PR Activation' },
    ];

  const generateMarketingBlueprintPDF = async (blueprintData, allData, setNotification) => {
    const { filmData, pressStrategy } = allData;
    const script_analysis = filmData?.script_analyses?.[filmData.script_analyses.length - 1]?.analysis;

    if (!blueprintData || !filmData) {
        setNotification({ type: 'error', message: 'Missing data for blueprint generation.' });
        return;
    }

    const audienceAnalysis = filmData?.ai_analyses?.[filmData.ai_analyses.length - 1]?.analysis;

    setNotification({ type: 'info', message: 'Formatting your Polished Blueprint...' });

    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ 
        orientation: 'p', 
        unit: 'mm', 
        format: 'a4',
        compress: true
    });

    // Page dimensions
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 20;
    const contentWidth = pageWidth - (2 * margin);
    let yPos = margin;
    let pageNumber = 1;

    // Colors
    const purple = [139, 92, 246];
    const darkGray = [31, 41, 55];
    const mediumGray = [75, 85, 99];
    const lightGray = [156, 163, 175];

    // Helper: Add new page
    const addNewPage = () => {
        doc.addPage();
        yPos = margin;
        pageNumber++;
    };

   // Helper: Check if we need new page
const checkPageBreak = (heightNeeded) => {
    if (yPos + heightNeeded > pageHeight - margin - 10) {
        addFooter();  // Add footer to current page before moving to next
        addNewPage();
        return true;
    }
    return false;
};

    // Helper: Add footer
    const addFooter = () => {
        doc.setFontSize(9);
        doc.setTextColor(...lightGray);
        doc.text(
            `${filmData.title} | Marketing Blueprint | Page ${pageNumber}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
        );
    };

    // Helper: Wrap text
    const wrapText = (text, maxWidth) => {
        return doc.splitTextToSize(text, maxWidth);
    };

    // Helper: Add wrapped text with proper spacing
    const addWrappedText = (text, fontSize = 10.5, lineHeight = 7, color = darkGray) => {
        doc.setFontSize(fontSize);
        doc.setTextColor(...color);
        const lines = wrapText(text, contentWidth);
        
        lines.forEach((line, index) => {
            if (checkPageBreak(lineHeight)) {
                addFooter();
            }
            doc.text(line, margin, yPos);
            yPos += lineHeight;
        });
        
        return lines.length;
    };

    // Helper: Add section title
    const addSectionTitle = (sectionNumber, title) => {
        checkPageBreak(20);
        
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        
        // Section number in purple
        doc.setTextColor(...purple);
        const numWidth = doc.getTextWidth(sectionNumber + ' ');
        doc.text(sectionNumber, margin, yPos);
        
        // Title in dark gray
        doc.setTextColor(...darkGray);
        doc.text(title, margin + numWidth, yPos);
        
        // Underline
        yPos += 3;
        doc.setDrawColor(...purple);
        doc.setLineWidth(0.8);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        
        yPos += 12;
    };

    // Helper: Add subsection title
    const addSubsectionTitle = (title) => {
        checkPageBreak(12);
        yPos += 5;
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...darkGray);
        doc.text(title, margin, yPos);
        yPos += 8;
    };

    // Helper: Add action items (bullet list)
    const addActionItems = (items) => {
        if (!items || items.length === 0) return;
        
        doc.setFont('helvetica', 'normal');
        const bulletX = margin + 2;
        const textX = margin + 8;
        
        items.forEach((item) => {
            const itemHeight = (wrapText(item, contentWidth - 8).length * 7) + 6;
            checkPageBreak(itemHeight);
            
            // Background box
            doc.setFillColor(249, 250, 251);
            doc.roundedRect(margin, yPos - 4, contentWidth, itemHeight, 2, 2, 'F');
            
            // Purple border
            doc.setDrawColor(...purple);
            doc.setLineWidth(1);
            doc.line(margin, yPos - 4, margin, yPos + itemHeight - 4);
            
            // Checkmark
            doc.setTextColor(...purple);
            doc.setFontSize(12);
            doc.text('✓', bulletX, yPos + 2);
            
            // Text
            doc.setFontSize(10);
            doc.setTextColor(...darkGray);
            const lines = wrapText(item, contentWidth - 8);
            lines.forEach((line, idx) => {
                doc.text(line, textX, yPos + 2 + (idx * 5));
            });
            
            yPos += itemHeight + 2;
        });
    };

    // Helper: Add standard section
    const addSection = (section, sectionNumber, sectionTitle) => {
        if (!section) return;
        
        addFooter();
        addNewPage();
        addSectionTitle(sectionNumber, sectionTitle);
        
        if (section.strategic_analysis) {
            addSubsectionTitle('Strategic Analysis');
            addWrappedText(section.strategic_analysis);
            yPos += 5;
        }
        
        if (section.action_items && section.action_items.length > 0) {
            addSubsectionTitle('Action Items');
            addActionItems(section.action_items);
        }
    };

    // === TITLE PAGE ===
    doc.setFontSize(36);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...purple);
    const titleLines = wrapText((filmData.title || "FILM TITLE").toUpperCase(), contentWidth);
    let titleY = 100;
    titleLines.forEach(line => {
        doc.text(line, pageWidth / 2, titleY, { align: 'center' });
        titleY += 15;
    });

    doc.setFontSize(15);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...mediumGray);
    doc.text('Marketing & PR Blueprint', pageWidth / 2, titleY + 10, { align: 'center' });

    // Fixed footer for title page - separate date from page number
    doc.setFontSize(9);
    doc.setTextColor(...lightGray);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth / 2, pageHeight - 15, { align: 'center' });

    // === SECTIONS ===
    addSection(blueprintData.executive_summary, '1.0', 'Executive Summary');
    addSection(blueprintData.audience_deep_dive, '2.0', 'Target Audience Deep Dive');
    addSection(blueprintData.creative_strategy, '3.0', 'Creative Strategy & Messaging');
    addSection(blueprintData.goto_market_plan, '4.0', 'Go-to-Market Plan');

    // === DISTRIBUTION STRATEGY ===
    if (audienceAnalysis) {
        addFooter();
        addNewPage();
        addSectionTitle('5.0', 'Distribution Strategy: Theaters & Festivals');

        // Two-column layout
        const colWidth = (contentWidth - 10) / 2;
        const col1X = margin;
        const col2X = margin + colWidth + 10;
        
        // Markets column
        addSubsectionTitle('Top U.S. Theatrical Markets');
        const marketsY = yPos;
        
        if (audienceAnalysis.top_us_theatrical_markets) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9.5);
            audienceAnalysis.top_us_theatrical_markets.slice(0, 15).forEach((market, idx) => {
                if (checkPageBreak(6)) {
                    addFooter();
                    addSubsectionTitle('Top U.S. Theatrical Markets (continued)');
                }
                doc.setTextColor(...mediumGray);
                doc.text(`${idx + 1}.`, col1X, yPos);
                doc.setTextColor(...darkGray);
                doc.text(market, col1X + 7, yPos);
                yPos += 6;
            });
        }
        
        // Festivals column (start at same Y as markets)
        yPos = marketsY;
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...darkGray);
        doc.text('Top Film Festivals', col2X, yPos);
        yPos += 8;

        const addFestivalTier = (tierName, festivals) => {
            if (!festivals || festivals.length === 0) return;
            
            checkPageBreak(8);
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...purple);
            doc.text(tierName, col2X, yPos);
            yPos += 6;
            
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            festivals.forEach(fest => {
                checkPageBreak(6);
                doc.setTextColor(...darkGray);
                const festText = `${fest.name} (${fest.dates})`;
                const lines = doc.splitTextToSize(festText, colWidth - 5);
                lines.forEach(line => {
                    doc.text(line, col2X, yPos);
                    yPos += 5;
                });
            });
            yPos += 3;
        };

        if (audienceAnalysis.top_film_festivals) {
            addFestivalTier('Tier 1 - Premiere & Major Markets', audienceAnalysis.top_film_festivals.tier_1);
            addFestivalTier('Tier 2 - Genre & Regional Focus', audienceAnalysis.top_film_festivals.tier_2);
            addFestivalTier('Tier 3 - Emerging & Niche', audienceAnalysis.top_film_festivals.tier_3);
        }
    }

    addSection(blueprintData.digital_pr_activation, '6.0', 'Digital & PR Activation');

    // === PRESS STRATEGY ===
    if (pressStrategy) {
        addFooter();
        addNewPage();
        addSectionTitle('7.0', 'Press & Public Relations (Strategy)');
        
        if (pressStrategy.executive_summary) {
            addSubsectionTitle('Executive Summary');
            addWrappedText(pressStrategy.executive_summary);
            yPos += 5;
        }
        
        if (pressStrategy.strategy_pillars && pressStrategy.strategy_pillars.length > 0) {
            addSubsectionTitle('Strategy Pillars');
            
            pressStrategy.strategy_pillars.forEach(pillar => {
                const pillarHeight = 25 + (wrapText(pillar.description, contentWidth - 10).length * 6);
                checkPageBreak(pillarHeight);
                
                // Pillar box
                doc.setFillColor(249, 250, 251);
                doc.roundedRect(margin, yPos, contentWidth, pillarHeight, 3, 3, 'F');
                doc.setDrawColor(...purple);
                doc.setLineWidth(1);
                doc.line(margin, yPos, margin, yPos + pillarHeight);
                
                // Pillar title
                doc.setFontSize(11);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(...darkGray);
                doc.text(pillar.pillar, margin + 5, yPos + 7);
                
                // Description
                doc.setFontSize(9.5);
                doc.setFont('helvetica', 'normal');
                const descLines = wrapText(pillar.description, contentWidth - 10);
                descLines.forEach((line, idx) => {
                    doc.text(line, margin + 5, yPos + 15 + (idx * 5.5));
                });
                
                yPos += pillarHeight + 5;
            });
        }

        // Press Outlets
        if (pressStrategy.target_outlets) {
            addFooter();
            addNewPage();
            addSectionTitle('7.1', 'Press & Public Relations (Targeted Outlets)');
            addSubsectionTitle('Target Outlets');

            const addOutletTable = (tierName, outlets) => {
                if (!outlets || outlets.length === 0) return;
                
                checkPageBreak(15);
                
                // Tier header
                doc.setFillColor(243, 244, 246);
                doc.rect(margin, yPos, contentWidth, 8, 'F');
                doc.setFontSize(9.5);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(107, 33, 168);
                doc.text(tierName, pageWidth / 2, yPos + 5.5, { align: 'center' });
                yPos += 8;
                
                // Outlets
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(9);
                outlets.forEach(outlet => {
                    checkPageBreak(8);
                    
                    const outletWidth = contentWidth * 0.3;
                    const angleWidth = contentWidth * 0.55;
                    const priorityWidth = contentWidth * 0.15;
                    
                    doc.setTextColor(...darkGray);
                    doc.text(outlet.outlet, margin, yPos + 4);
                    
                    const angleLines = doc.splitTextToSize(outlet.angle, angleWidth);
                    angleLines.forEach((line, idx) => {
                        doc.text(line, margin + outletWidth, yPos + 4 + (idx * 5));
                    });
                    
                    const priorityColors = {
                        'High': [185, 28, 28],
                        'Medium': [161, 98, 7],
                        'Low': [22, 101, 52]
                    };
                    doc.setTextColor(...(priorityColors[outlet.priority] || mediumGray));
                    doc.setFont('helvetica', 'bold');
                    doc.text(outlet.priority || 'N/A', margin + outletWidth + angleWidth, yPos + 4);
                    doc.setFont('helvetica', 'normal');
                    
                    const rowHeight = Math.max(8, angleLines.length * 5 + 3);
                    yPos += rowHeight;
                    
                    doc.setDrawColor(229, 231, 235);
                    doc.setLineWidth(0.3);
                    doc.line(margin, yPos, pageWidth - margin, yPos);
                });
                
                yPos += 5;
            };

            addOutletTable('Tier 1 - Major Publications', pressStrategy.target_outlets.tier_1);
            addOutletTable('Tier 2 - Industry & Regional', pressStrategy.target_outlets.tier_2);
            addOutletTable('Niche & Specialized', pressStrategy.target_outlets.niche_specialized);
        }

        // Press Timeline
if (pressStrategy.timeline) {
    addFooter();  // Footer for previous page
    addNewPage();
    addSectionTitle('7.2', 'Press & Public Relations (Timeline)');
    addSubsectionTitle('Campaign Timeline');

    pressStrategy.timeline.forEach(phase => {
        const phaseHeight = 30 + 
            (wrapText((phase.activities || []).join(', '), contentWidth - 10).length * 5) +
            (wrapText((phase.deliverables || []).join(', '), contentWidth - 10).length * 5);
        
        checkPageBreak(phaseHeight);
        
        // Phase box
        doc.setFillColor(249, 250, 251);
        doc.roundedRect(margin, yPos, contentWidth, phaseHeight, 3, 3, 'F');
        doc.setDrawColor(74, 222, 128);
        doc.setLineWidth(1);
        doc.line(margin, yPos, margin, yPos + phaseHeight);
        
        // Phase title on LEFT
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...darkGray);
        doc.text(phase.phase, margin + 5, yPos + 7);
        
        // Timeframe on RIGHT (right-aligned)
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...mediumGray);
        doc.text(`(${phase.timeframe})`, pageWidth - margin - 5, yPos + 7, { align: 'right' });
        
        let textY = yPos + 15;
        
        // Activities
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...darkGray);
        doc.text('Activities:', margin + 5, textY);
        doc.setFont('helvetica', 'normal');
        const actLines = wrapText((phase.activities || []).join(', '), contentWidth - 10);
        actLines.forEach((line, idx) => {
            doc.text(line, margin + 5, textY + 5 + (idx * 5));
        });
        textY += 5 + (actLines.length * 5);
        
        // Deliverables
        doc.setFont('helvetica', 'bold');
        doc.text('Deliverables:', margin + 5, textY);
        doc.setFont('helvetica', 'normal');
        const delLines = wrapText((phase.deliverables || []).join(', '), contentWidth - 10);
        delLines.forEach((line, idx) => {
            doc.text(line, margin + 5, textY + 5 + (idx * 5));
        });
        
        yPos += phaseHeight + 5;
    });
    
    // IMPORTANT: Add footer to Press Timeline page
    addFooter();
}
    }

    // === OPTIMAL RELEASE STRATEGY ===
    if (script_analysis?.optimal_release_strategy) {
        addFooter();
        addNewPage();
        addSectionTitle('8.0', 'Optimal Release Strategy');
        
        const ors = script_analysis.optimal_release_strategy;
        
        if (ors.recommended_window) {
            addSubsectionTitle('Recommended Window');
            addWrappedText(ors.recommended_window);
            yPos += 5;
        }
        
        if (ors.strategic_timing) {
            addSubsectionTitle('Strategic Rationale');
            addWrappedText(ors.strategic_timing);
            yPos += 5;
        }
        
        if (ors.avoid_periods && ors.avoid_periods.length > 0) {
            addSubsectionTitle('Periods to Avoid');
            addActionItems(ors.avoid_periods);
        }
    }

    // Final footer
    addFooter();

    // Save
    doc.save(`${filmData.title.replace(/\s+/g, '_')}_Marketing_Blueprint.pdf`);
    setNotification({ type: 'success', message: 'Polished blueprint downloaded successfully!' });
};

    const handleGenerate = async () => {
        if (!hasRequiredData) {
            setNotification({ type: 'error', message: 'Please run Audience Genius and generate a Budget Plan before creating a blueprint.' });
            return;
        }
        setIsLoading(true);
        
        try {
            const blueprintJSON = {};
            const totalSections = sectionsToGenerate.length;

            for (let i = 0; i < totalSections; i++) {
                setProgress(`Generating Section ${i + 1}/${totalSections}: ${sectionsToGenerate[i].name}...`);
                blueprintJSON[sectionsToGenerate[i].key] = await generateBlueprintSection(sectionsToGenerate[i].name, allData);
            }

            setProgress('Assembling PDF...');
            await generateMarketingBlueprintPDF(blueprintJSON, allData, setNotification);

        } catch (error) {
            setNotification({ type: 'error', message: `Blueprint generation failed: ${error.message}` });
        } finally {
            setIsLoading(false);
            setProgress('');
        }
    };

    return (
        <div className="bg-zinc-900/50 backdrop-blur-md border border-zinc-700 p-4 rounded-xl max-w-sm text-left shadow-lg">
            <h3 className="font-bold text-white mb-2 flex items-center gap-2"><FileText size={18}/> Marketing Blueprint</h3>
            <p className="text-xs text-zinc-300 mb-3">Synthesize all data into a polished, multi-page strategic plan for export.</p>
            <AppButton onClick={handleGenerate} disabled={isLoading || !hasRequiredData} size="md" icon={isLoading ? null : Download} className="w-full">
                {isLoading ? (
                    <div className="flex items-center gap-2">
                        <BlackboxLoaderIcon className="w-5 h-5" />
                        <span>{progress || 'Initializing...'}</span>
                    </div>
                ) : (
                    'Generate & Export Blueprint'
                )}
            </AppButton>
            {!hasRequiredData && (
                <p className="text-yellow-400 text-xs mt-2 text-center">
                    Requires 'Audience Genius' & 'Budget Planner'.
                </p>
            )}
        </div>
    );
};


// ========================================
// PRODUCTION-READY PDF EXPORT SYSTEM
// ========================================


// --- ROBUST IMAGE LOADER (Canvas Method) ---
const getBase64ImageFromUrl = (url) => {
    return new Promise((resolve) => {
        if (!url) { resolve(null); return; }
        
        const img = new Image();
        // this is crucial for Firebase/S3 images
        img.setAttribute('crossOrigin', 'anonymous'); 
        
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            try {
                const dataURL = canvas.toDataURL('image/jpeg', 0.8);
                resolve(dataURL);
            } catch (error) {
                // This catches "Tainted Canvas" errors (CORS blocks)
                console.warn("CORS Blocked the image export. Using placeholder.");
                resolve(null); 
            }
        };

        img.onerror = () => {
            console.warn("Image failed to load.");
            resolve(null);
        };

        // Anti-cache param to force fresh load
        img.src = url + '?t=' + new Date().getTime(); 
    });
};

// --- MASTER STUDIO DOSSIER EXPORT ENGINE (V3.1 FINAL) ---
// --- MASTER STUDIO DOSSIER EXPORT ENGINE (V3.2 - DYNAMIC LAYOUTS) ---
const handleExportMasterDossier = async (allData, setNotification) => {
    try {
        setNotification({ type: 'loading', message: 'Rendering Studio PDF... (Processing Data)' });
        
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF('p', 'pt', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 40;
        let y = margin;

        // --- DESIGN SYSTEM ---
        const COLORS = {
            BLACK: [18, 18, 20],      
            TEXT: [63, 63, 70],       
            MUTED: [161, 161, 170],   
            EMERALD: [16, 185, 129],  
            PURPLE: [147, 51, 234],   
            RED: [220, 38, 38],       
            BLUE: [37, 99, 235],
            ACCENT: [255, 255, 255]
        };

        const FONTS = { HEAD: "helvetica", BODY: "helvetica", MONO: "courier" };

        // --- HELPER UTILITIES ---
        const formatCurrency = (val) => {
            if (!val || isNaN(val)) return "$0";
            if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
            if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
            return `$${val}`;
        };

        const checkPageBreak = (neededHeight) => {
            if (y + neededHeight > pageHeight - margin) {
                doc.addPage();
                y = margin;
                return true;
            }
            return false;
        };

        const drawSectionHeader = (number, title, color = COLORS.BLACK) => {
            checkPageBreak(60);
            y += 20;
            doc.setFillColor(...color);
            doc.roundedRect(margin, y, 30, 18, 4, 4, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFont(FONTS.HEAD, "bold");
            doc.setFontSize(10);
            doc.text(number, margin + 15, y + 12, { align: 'center' });
            doc.setTextColor(...color);
            doc.setFontSize(14);
            doc.text(title.toUpperCase(), margin + 40, y + 14);
            y += 25;
            doc.setDrawColor(230, 230, 230);
            doc.setLineWidth(1);
            doc.line(margin, y, pageWidth - margin, y);
            y += 25;
        };

        const drawMetricCard = (x, y, label, value, subtext, color = COLORS.BLACK) => {
            doc.setFillColor(252, 252, 252);
            doc.setDrawColor(230, 230, 230);
            doc.roundedRect(x, y, 160, 70, 4, 4, 'FD');
            doc.setFont(FONTS.HEAD, "bold");
            doc.setFontSize(9);
            doc.setTextColor(...COLORS.MUTED);
            doc.text(label.toUpperCase(), x + 15, y + 20);
            doc.setFont(FONTS.MONO, "bold");
            doc.setFontSize(18);
            doc.setTextColor(...color);
            doc.text(value?.toString() || "N/A", x + 15, y + 45);
            if (subtext) {
                doc.setFont(FONTS.HEAD, "normal");
                doc.setFontSize(8);
                doc.setTextColor(...COLORS.MUTED);
                doc.text(subtext, x + 15, y + 60);
            }
        };

        const drawEmptySection = (height, title, subtitle) => {
            doc.setDrawColor(220, 220, 220);
            doc.setLineWidth(1.5);
            doc.setLineDash([6, 4], 0);
            doc.setFillColor(252, 252, 252);
            doc.roundedRect(margin, y, pageWidth - (margin * 2), height, 8, 8, 'FD');
            doc.setLineDash([], 0); 
            doc.setTextColor(...COLORS.MUTED);
            doc.setFont(FONTS.HEAD, "bold");
            doc.setFontSize(14);
            doc.text(`${title.toUpperCase()} // PENDING`, pageWidth / 2, y + (height / 2) - 10, { align: 'center' });
            doc.setFontSize(10);
            doc.setFont(FONTS.BODY, "normal");
            doc.text(subtitle, pageWidth / 2, y + (height / 2) + 10, { align: 'center' });
            y += height + 30;
        };

        // --- DATA LOADING ---
        const filmData = allData.filmData || {};
        const revenue = filmData.revenue_forecast?.data || null;
        const greenlight = filmData.greenlight_coverage || null;
        const budget = filmData.budget_plan || null;
        const competitors = filmData.competitor_analysis?.analysis || null;
        const audience = filmData.ai_analyses?.[filmData.ai_analyses.length - 1]?.analysis || null;

        // Attempt Load Poster
        let posterBase64 = null;
        if (filmData.poster || filmData.posterUrl) {
            posterBase64 = await getBase64ImageFromUrl(filmData.poster || filmData.posterUrl);
        }

        // ==========================================
        // PAGE 1: THE STUDIO COVER
        // ==========================================
        doc.setFillColor(...COLORS.BLACK);
        doc.rect(0, 0, pageWidth, pageHeight, 'F'); 

        // Accents
        doc.setDrawColor(...COLORS.PURPLE);
        doc.setLineWidth(2);
        doc.line(margin, margin, pageWidth - margin, margin);
        doc.line(margin, pageHeight - margin, pageWidth - margin, pageHeight - margin); 

        // --- POSTER OR PLACEHOLDER ---
        let titleY = pageHeight / 2 - 40;
        
        if (posterBase64) {
            try {
                const imgWidth = 220;
                const imgHeight = 330; 
                const imgX = (pageWidth - imgWidth) / 2;
                const imgY = margin + 60;
                doc.setDrawColor(...COLORS.PURPLE);
                doc.setLineWidth(1);
                doc.rect(imgX - 4, imgY - 4, imgWidth + 8, imgHeight + 8, 'S');
                doc.addImage(posterBase64, 'JPEG', imgX, imgY, imgWidth, imgHeight);
                titleY = imgY + imgHeight + 80; 
            } catch (err) {
                console.warn("Poster render error:", err);
                posterBase64 = null; 
            }
        } 
        
        if (!posterBase64) {
            const cardHeight = 200;
            const cardY = (pageHeight / 2) - 120;
            doc.setDrawColor(255, 255, 255);
            doc.setLineWidth(0.5);
            doc.rect(margin + 40, cardY, pageWidth - (margin * 2) - 80, cardHeight, 'S');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);
            doc.text("PROJECT DOSSIER", pageWidth / 2, cardY + 30, { align: 'center' });
            titleY = cardY + 110; 
        }

        doc.setTextColor(255, 255, 255);
        doc.setFont(FONTS.HEAD, "bold");
        doc.setFontSize(42);
        const titleText = (filmData.title || "UNTITLED PROJECT").toUpperCase();
        const titleLines = doc.splitTextToSize(titleText, pageWidth - (margin * 2));
        doc.text(titleLines, pageWidth / 2, titleY, { align: 'center' });

        if (posterBase64) {
             doc.setFontSize(14);
             doc.setFont(FONTS.HEAD, "normal");
             doc.setTextColor(180, 180, 180);
             doc.text("STRATEGIC GREENLIGHT DOSSIER", pageWidth / 2, titleY + (titleLines.length * 40) + 10, { align: 'center' });
        }

        doc.setFontSize(10);
        doc.text(`GENRE: ${filmData.genre || 'N/A'}  |  BUDGET TIER: ${filmData.budgetLevel || 'N/A'}`, pageWidth / 2, pageHeight - 80, { align: 'center' });
        doc.setTextColor(...COLORS.PURPLE);
        doc.text("CONFIDENTIAL // INTERNAL USE ONLY", pageWidth / 2, pageHeight - 60, { align: 'center' });

        // ==========================================
        // PAGE 2: EXECUTIVE VERDICT
        // ==========================================
        doc.addPage();
        y = margin;

        if (greenlight) {
            const verdictColor = greenlight.verdict === 'Greenlight' ? COLORS.EMERALD : greenlight.verdict === 'Yellow Light' ? [234, 179, 8] : COLORS.RED;
            
            doc.setFillColor(...verdictColor);
            doc.roundedRect(margin, y, pageWidth - (margin * 2), 80, 6, 6, 'F');
            
            doc.setTextColor(255, 255, 255);
            doc.setFont(FONTS.HEAD, "bold");
            doc.setFontSize(12);
            doc.text("EXECUTIVE VERDICT", margin + 30, y + 30);
            
            doc.setFontSize(36);
            doc.text((greenlight.verdict || "PENDING").toUpperCase(), margin + 30, y + 65);

            doc.setFontSize(10);
            doc.text("COMMERCIAL", pageWidth - margin - 150, y + 30);
            doc.setFontSize(24);
            doc.text(`${greenlight.commercial_score || 0}/100`, pageWidth - margin - 150, y + 60);

            doc.setFontSize(10);
            doc.text("CREATIVE", pageWidth - margin - 70, y + 30);
            doc.setFontSize(24);
            doc.text(`${greenlight.creative_score || 0}/100`, pageWidth - margin - 70, y + 60);

            y += 110;

            drawSectionHeader("01", "Executive Assessment", COLORS.BLACK);
            doc.setFont(FONTS.BODY, "normal");
            doc.setFontSize(11);
            doc.setTextColor(...COLORS.TEXT);
            const summaryLines = doc.splitTextToSize(greenlight.the_verdict_summary || "Summary pending.", pageWidth - (margin * 2));
            doc.text(summaryLines, margin, y);
            y += (summaryLines.length * 16) + 20;

            const leftX = margin;
            doc.setFont(FONTS.HEAD, "bold");
            doc.setFontSize(10);
            
            doc.setTextColor(...COLORS.EMERALD);
            doc.text("MAJOR STRENGTHS", leftX, y);
            y += 15;
            doc.setFont(FONTS.BODY, "normal");
            doc.setTextColor(...COLORS.TEXT);
            (greenlight.strengths || []).forEach(s => {
                const lines = doc.splitTextToSize(`• ${s}`, pageWidth - (margin * 2));
                doc.text(lines, leftX, y);
                y += (lines.length * 14) + 5;
            });

            y += 20;
            doc.setFont(FONTS.HEAD, "bold");
            doc.setTextColor(...COLORS.RED);
            doc.text("CRITICAL RISKS", leftX, y);
            y += 15;
            doc.setFont(FONTS.BODY, "normal");
            doc.setTextColor(...COLORS.TEXT);
            (greenlight.weaknesses || []).forEach(w => {
                const lines = doc.splitTextToSize(`• ${w}`, pageWidth - (margin * 2));
                doc.text(lines, leftX, y);
                y += (lines.length * 14) + 5;
            });
        } else {
            drawSectionHeader("01", "Executive Assessment", COLORS.BLACK);
            drawEmptySection(200, "Greenlight Coverage", "Upload script and run coverage.");
        }

        // ==========================================
        // PAGE 3: FINANCIAL INTELLIGENCE
        // ==========================================
        doc.addPage();
        y = margin;
        drawSectionHeader("02", "Financial Intelligence", COLORS.EMERALD);

        if (revenue) {
            if (revenue.scenarios) {
                const scenarios = [
                    { name: "CONSERVATIVE", data: revenue.scenarios.conservative, color: COLORS.MUTED },
                    { name: "REALISTIC", data: revenue.scenarios.realistic, color: COLORS.BLUE },
                    { name: "BREAKOUT", data: revenue.scenarios.breakout, color: COLORS.EMERALD }
                ];

                const cardWidth = (pageWidth - (margin * 2) - 20) / 3;
                let xOffset = margin;
                let maxVal = 0;

                scenarios.forEach(s => {
                    if(!s.data) return;
                    const total = (s.data.domestic_box_office || 0) + (s.data.international_box_office || 0);
                    if (total > maxVal) maxVal = total;
                    doc.setFillColor(250, 250, 250);
                    doc.setDrawColor(...s.color);
                    doc.setLineWidth(1.5);
                    doc.roundedRect(xOffset, y, cardWidth, 80, 4, 4, 'FD');
                    doc.setFontSize(9);
                    doc.setFont(FONTS.HEAD, "bold");
                    doc.setTextColor(...s.color);
                    doc.text(s.name, xOffset + 15, y + 20);
                    doc.setFontSize(14);
                    doc.setTextColor(...COLORS.BLACK);
                    doc.text(formatCurrency(total), xOffset + 15, y + 45);
                    xOffset += cardWidth + 10;
                });
                y += 100;

                // CHART
                if (maxVal > 0) {
                    doc.setFontSize(10);
                    doc.setTextColor(...COLORS.BLACK);
                    doc.text("SCENARIO FORECAST VISUALIZATION", margin, y);
                    y += 15;
                    const chartHeight = 120;
                    const chartBaseY = y + chartHeight;
                    const chartWidth = pageWidth - (margin * 2);
                    const barWidth = chartWidth / 7;
                    doc.setDrawColor(200, 200, 200);
                    doc.setLineWidth(1);
                    doc.line(margin, chartBaseY, pageWidth - margin, chartBaseY);
                    scenarios.forEach((s, idx) => {
                        if(!s.data) return;
                        const total = (s.data.domestic_box_office || 0) + (s.data.international_box_office || 0);
                        const barHeight = (total / maxVal) * (chartHeight - 20);
                        const xPos = margin + (barWidth) + (idx * (barWidth * 2));
                        doc.setFillColor(...s.color);
                        doc.rect(xPos, chartBaseY - barHeight, barWidth, barHeight, 'F');
                        doc.setFontSize(8);
                        doc.setTextColor(...COLORS.TEXT);
                        doc.text(formatCurrency(total), xPos + (barWidth/2), chartBaseY - barHeight - 5, { align: 'center'});
                    });
                    y += chartHeight + 30;
                }
            }

            if (revenue.greenlight_analysis) {
                checkPageBreak(120);
                doc.setFontSize(12);
                doc.setFont(FONTS.HEAD, "bold");
                doc.setTextColor(...COLORS.BLACK);
                doc.text("ROI ANALYSIS", margin, y);
                y += 20;
                const ga = revenue.greenlight_analysis;
                drawMetricCard(margin, y, "Break-Even", formatCurrency(ga.break_even_point), "Gross Revenue Needed");
                drawMetricCard(margin + 170, y, "Budget Cap", formatCurrency(ga.recommended_budget_cap), "Max Spend");
                drawMetricCard(margin + 340, y, "ROI (Realistic)", `${ga.roi_multiple || 'N/A'}x`, "Return Multiple");
                y += 90;
            }
        } else {
            drawEmptySection(250, "Revenue Models", "Run the Revenue Forecaster.");
        }

        // ==========================================
        // PAGE 4: CAPITAL STRATEGY (Dynamic Red Box)
        // ==========================================
        checkPageBreak(300);
        if (y > pageHeight - 300) { doc.addPage(); y = margin; }
        drawSectionHeader("03", "Capital Strategy", COLORS.BLUE);

        if (budget) {
            if (budget.phased_timeline) {
                doc.setFontSize(12);
                doc.setTextColor(...COLORS.BLACK);
                doc.text("PHASED CASH FLOW DEPLOYMENT", margin, y);
                y += 20;
                budget.phased_timeline.forEach((phase) => {
                    checkPageBreak(60);
                    doc.setDrawColor(...COLORS.MUTED);
                    doc.setLineWidth(0.5);
                    doc.line(margin + 6, y, margin + 6, y + 50);
                    doc.setFillColor(...COLORS.BLUE);
                    doc.circle(margin + 6, y + 6, 3, 'F');
                    doc.setFontSize(11);
                    doc.setFont(FONTS.HEAD, "bold");
                    doc.setTextColor(...COLORS.BLACK);
                    doc.text(phase.phase, margin + 25, y + 10);
                    doc.setFontSize(9);
                    doc.setFont(FONTS.BODY, "normal");
                    doc.setTextColor(...COLORS.TEXT);
                    const activities = (phase.activities || []).slice(0, 3).join(" • ");
                    const lines = doc.splitTextToSize(activities, pageWidth - margin - 40);
                    doc.text(lines, margin + 25, y + 25);
                    y += (lines.length * 12) + 20;
                });
            }

            // --- DYNAMIC RED BOX FIX ---
            if (budget.risk_and_flex_budget) {
                checkPageBreak(150);
                y += 10;
                
                const riskText = budget.risk_and_flex_budget.trigger_kpis || "N/A";
                // 1. Calculate height first
                doc.setFontSize(10);
                doc.setFont(FONTS.BODY, "normal");
                const riskLines = doc.splitTextToSize(riskText, pageWidth - (margin * 2) - 30);
                const textHeight = riskLines.length * 14;
                const boxHeight = textHeight + 50; // Text height + Padding top/bottom

                // 2. Draw box with calculated height
                doc.setFillColor(255, 245, 245); 
                doc.setDrawColor(...COLORS.RED);
                doc.roundedRect(margin, y, pageWidth - (margin * 2), boxHeight, 4, 4, 'FD');
                
                doc.setTextColor(...COLORS.RED);
                doc.setFont(FONTS.HEAD, "bold");
                doc.text("RISK CONTINGENCY PROTOCOL", margin + 15, y + 20);
                
                doc.setTextColor(...COLORS.BLACK);
                doc.setFont(FONTS.BODY, "normal");
                doc.text(riskLines, margin + 15, y + 35);
                
                y += boxHeight + 20;
            }
        } else {
            drawEmptySection(250, "Budget Blueprint", "Run the Budget Planner.");
        }

        // ==========================================
        // PAGE 5: WAR ROOM
        // ==========================================
        doc.addPage();
        y = margin;
        drawSectionHeader("04", "Competitive Landscape", COLORS.RED);

        if (competitors) {
            if (competitors.tale_of_the_tape) {
                doc.setFontSize(12);
                doc.setTextColor(...COLORS.BLACK);
                doc.text("TALE OF THE TAPE: COMPARABLE ANALYSIS", margin, y);
                y += 20;
                const tape = competitors.tale_of_the_tape;
                const stats = [
                    { l: "Verdict", v: tape.box_office_verdict },
                    { l: "Est. Budget", v: tape.budget_estimated },
                    { l: "Opening", v: tape.opening_weekend },
                    { l: "Audience Score", v: tape.audience_score }
                ];
                let xPos = margin;
                stats.forEach(s => {
                    drawMetricCard(xPos, y, s.l, s.v, "");
                    xPos += 130; 
                });
                y += 90;
            }
            if (competitors.battle_plan) {
                doc.setFillColor(30, 30, 30);
                doc.rect(margin, y, pageWidth - (margin * 2), 25, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(10);
                doc.text("STRATEGIC BATTLE PLAN", margin + 10, y + 17);
                y += 40;
                const planItems = [
                    { l: "STEAL", t: competitors.battle_plan.steal_this, c: COLORS.BLUE },
                    { l: "EXPLOIT", t: competitors.battle_plan.exploit_this, c: COLORS.EMERALD },
                    { l: "IGNORE", t: competitors.battle_plan.ignore_this, c: COLORS.RED }
                ];
                planItems.forEach(item => {
                    checkPageBreak(60);
                    doc.setFont(FONTS.HEAD, "bold");
                    doc.setTextColor(...item.c);
                    doc.text(item.l, margin, y);
                    doc.setFont(FONTS.BODY, "normal");
                    doc.setTextColor(...COLORS.TEXT);
                    const lines = doc.splitTextToSize(item.t || "N/A", pageWidth - (margin * 2) - 80);
                    doc.text(lines, margin + 80, y);
                    y += (lines.length * 14) + 15;
                });
            }
        } else {
            drawEmptySection(200, "Competitive Forensics", "Run the War Room.");
        }

        // ==========================================
        // PAGE 6: AUDIENCE DEEP DIVE (Expanded)
        // ==========================================
        checkPageBreak(300);
        if (y > pageHeight - 300) { doc.addPage(); y = margin; }
        drawSectionHeader("05", "Audience Architecture", COLORS.PURPLE);
        
        if (audience) {
            const breakdown = audience.target_audience_breakdown || {};
            
            // 1. PRIMARY PROFILE (Persona)
            doc.setFillColor(250, 250, 255);
            doc.setDrawColor(...COLORS.PURPLE);
            doc.roundedRect(margin, y, pageWidth - (margin * 2), 70, 4, 4, 'FD');
            
            doc.setFont(FONTS.HEAD, "bold");
            doc.setFontSize(10);
            doc.setTextColor(...COLORS.PURPLE);
            doc.text("PRIMARY TARGET PERSONA", margin + 15, y + 20);
            
            doc.setFont(FONTS.BODY, "bold");
            doc.setFontSize(14);
            doc.setTextColor(...COLORS.BLACK);
            doc.text(breakdown.primary_audience_profile || "Analysis Pending", margin + 15, y + 45);
            y += 90;

            // 2. DEMOGRAPHICS & PSYCHOGRAPHICS GRID
            const colWidth = (pageWidth - (margin * 2)) / 2;
            
            // Left Col: Demographics
            doc.setFont(FONTS.HEAD, "bold");
            doc.setFontSize(11);
            doc.setTextColor(...COLORS.BLACK);
            doc.text("DEMOGRAPHICS", margin, y);
            
            doc.setFont(FONTS.BODY, "normal");
            doc.setTextColor(...COLORS.TEXT);
            doc.setFontSize(10);
            const demoLines = doc.splitTextToSize(breakdown.demographics || "N/A", colWidth - 20);
            doc.text(demoLines, margin, y + 15);
            
            // Right Col: Psychographics (Why they watch)
            doc.setFont(FONTS.HEAD, "bold");
            doc.setTextColor(...COLORS.BLACK);
            doc.setFontSize(11);
            doc.text("PSYCHOGRAPHICS (DRIVERS)", margin + colWidth, y);
            
            doc.setFont(FONTS.BODY, "normal");
            doc.setTextColor(...COLORS.TEXT);
            doc.setFontSize(10);
            const psychLines = doc.splitTextToSize(breakdown.psychographics || "N/A", colWidth);
            doc.text(psychLines, margin + colWidth, y + 15);
            
            y += Math.max(demoLines.length, psychLines.length) * 14 + 30;

            // 3. VIEWING HABITS (If available)
            if (breakdown.viewing_habits) {
                checkPageBreak(60);
                doc.setFont(FONTS.HEAD, "bold");
                doc.setFontSize(11);
                doc.setTextColor(...COLORS.BLACK);
                doc.text("VIEWING HABITS & PLATFORMS", margin, y);
                y += 15;
                const habitLines = doc.splitTextToSize(breakdown.viewing_habits, pageWidth - (margin * 2));
                doc.setFont(FONTS.BODY, "normal");
                doc.text(habitLines, margin, y);
                y += (habitLines.length * 14) + 20;
            }

            // 4. MARKETING PILLARS
            if (audience.marketing_pillars_strategy?.pillars) {
                checkPageBreak(80);
                doc.setFont(FONTS.HEAD, "bold");
                doc.setFontSize(11);
                doc.setTextColor(...COLORS.BLACK);
                doc.text("CORE MARKETING PILLARS", margin, y);
                y += 15;
                audience.marketing_pillars_strategy.pillars.slice(0, 4).forEach(p => {
                    const line = `• ${p.pillar}`;
                    doc.setFont(FONTS.BODY, "normal");
                    doc.setTextColor(...COLORS.TEXT);
                    doc.text(line, margin, y);
                    y += 14;
                });
            }
        } else {
            drawEmptySection(200, "Audience DNA", "Run Audience Analysis.");
        }

        // FOOTER
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            if (i === 1) continue; 
            doc.setFontSize(8);
            doc.setTextColor(...COLORS.MUTED);
            doc.text(`BLACKBOX mi // Confidential // Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 20, { align: 'center' });
        }

        const safeTitle = (filmData.title || "Project").replace(/[^a-z0-9]/gi, '_');
        doc.save(`${safeTitle}_MASTER_GREENLIGHT_DOSSIER.pdf`);
        setNotification({ type: 'success', message: 'Studio Dossier Generated Successfully.' });

    } catch (error) {
        console.error("Master Export Error:", error);
        setNotification({ type: 'error', message: 'Export failed. Check console.' });
    }
};

function _dossierPdfStyles(orientation, colors, options, baseFontSize, pageWidth, pageHeight, contentPadding, posterUrl = '') {
    // Extracted verbatim from the original inline <style> block so generateModernDossierPDF,
    // generateSocialPlanPDF, and generatePRPlanPDF all render with identical typography/layout —
    // one shared stylesheet instead of three copies that could drift apart.
    return `
      <style>
        @page {
          size: A4 ${orientation};
          margin: 0;
          -webkit-print-color-adjust: exact;
          color-adjust: exact;
        }
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          line-height: 1.5;
          color: ${colors.text || '#1f2937'};
          background: ${options.darkBackground ? '#0f172a' : 'white'};
          font-size: ${baseFontSize};
        }
        
        .page {
          width: ${pageWidth};
          min-height: ${pageHeight};
          padding: ${contentPadding};
          page-break-after: always;
          position: relative;
          background: ${options.darkBackground ? '#0f172a' : 'white'};
          overflow: hidden;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
        }
        
        .page:last-child {
          page-break-after: avoid;
        }
        
        .page-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        
        .title-page {
          background: linear-gradient(135deg, rgba(0,0,0,0.7), rgba(0,0,0,0.5)), 
                      url('${posterUrl}');
          background-size: cover;
          background-position: center;
          color: white;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
          padding: ${contentPadding};
        }
        
        .title-page h1 {
          font-size: ${orientation === 'landscape' ? '2.8rem' : '3.5rem'};
          font-weight: 900;
          margin-bottom: 1rem;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.8);
          word-wrap: break-word;
          max-width: 90%;
        }
        
        .title-page .subtitle {
          font-size: ${orientation === 'landscape' ? '1.2rem' : '1.5rem'};
          margin-bottom: 2rem;
          opacity: 0.95;
        }
        
        .title-page .logline {
          font-size: ${orientation === 'landscape' ? '1rem' : '1.1rem'};
          max-width: ${orientation === 'landscape' ? '80%' : '600px'};
          margin: 0 auto;
          font-style: italic;
          opacity: 0.9;
          line-height: 1.6;
        }
        
        .header {
          background: ${colors.gradient};
          color: white;
          padding: ${orientation === 'landscape' ? '1.2rem' : '2rem'};
          margin: -${contentPadding} -${contentPadding} 1.5rem -${contentPadding};
          text-align: center;
          flex-shrink: 0;
        }
        
        .header h1 {
          font-size: ${orientation === 'landscape' ? '1.8rem' : '2.2rem'};
          margin-bottom: 0.5rem;
          word-wrap: break-word;
        }
        
        .header .subtitle {
          font-size: ${orientation === 'landscape' ? '0.95rem' : '1.1rem'};
          opacity: 0.9;
        }
        
        .section {
          margin-bottom: ${orientation === 'landscape' ? '1.2rem' : '1.8rem'};
          page-break-inside: avoid;
          flex-shrink: 0;
        }
        
        .section-header {
          background: ${colors.primary};
          color: white;
          padding: ${orientation === 'landscape' ? '0.8rem' : '1rem'};
          font-size: ${orientation === 'landscape' ? '1.1rem' : '1.3rem'};
          font-weight: bold;
          margin-bottom: ${orientation === 'landscape' ? '0.8rem' : '1rem'};
          border-radius: 6px;
          word-wrap: break-word;
        }
        
        .content-grid {
          display: grid;
          grid-template-columns: ${orientation === 'landscape' ? 'repeat(3, 1fr)' : '1fr 1fr'};
          gap: ${orientation === 'landscape' ? '1.2rem' : '1.5rem'};
          margin-bottom: ${orientation === 'landscape' ? '1rem' : '1.5rem'};
        }
        
        .content-card {
          background: ${options.darkBackground ? '#1e293b' : '#f8fafc'};
          padding: ${orientation === 'landscape' ? '1rem' : '1.2rem'};
          border-radius: 8px;
          border-left: 4px solid ${colors.primary};
          height: fit-content;
          page-break-inside: avoid;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        
        .content-card h3 {
          color: ${colors.primary};
          margin-bottom: 0.6rem;
          font-size: ${orientation === 'landscape' ? '1rem' : '1.1rem'};
          word-wrap: break-word;
        }
        
        .content-card p {
          font-size: ${orientation === 'landscape' ? '0.85rem' : '0.9rem'};
          line-height: 1.5;
          word-wrap: break-word;
          hyphens: auto;
          overflow-wrap: break-word;
        }
        
        .metric-item {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 0.3rem 0;
          border-bottom: 1px solid ${options.darkBackground ? '#374151' : '#e2e8f0'};
          font-size: ${orientation === 'landscape' ? '0.8rem' : '0.9rem'};
          word-wrap: break-word;
        }
        
        .metric-item:last-child {
          border-bottom: none;
        }
        
        .metric-label {
          font-weight: 600;
          color: ${options.darkBackground ? '#d1d5db' : (colors.text || '#1f2937')};
          flex: 0 0 auto;
          margin-right: 1rem;
          word-wrap: break-word;
          min-width: 100px;
        }
        
        .metric-value {
          color: ${colors.primary};
          font-weight: bold;
          text-align: right;
          word-break: break-word;
          flex: 1;
          overflow-wrap: break-word;
        }
        
        .highlight-box {
          background: linear-gradient(135deg, ${colors.primary}15, ${colors.secondary}15);
          border: 2px solid ${colors.primary}30;
          padding: ${orientation === 'landscape' ? '1.2rem' : '1.5rem'};
          border-radius: 8px;
          margin: 1rem 0;
          page-break-inside: avoid;
        }
        
        .poster-container {
          text-align: center;
          margin: 1.5rem 0;
          page-break-inside: avoid;
        }
        
        .poster-container img {
          max-width: ${orientation === 'landscape' ? '180px' : '220px'};
          max-height: ${orientation === 'landscape' ? '240px' : '300px'};
          border-radius: 8px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.3);
        }
        
        .chart-container {
          background: ${options.darkBackground ? '#1e293b' : 'white'};
          padding: ${orientation === 'landscape' ? '1rem' : '1.2rem'};
          border-radius: 8px;
          border: 1px solid ${options.darkBackground ? '#374151' : '#e2e8f0'};
          margin: 1rem 0;
          page-break-inside: avoid;
        }
        
        .chart-bar {
          display: flex;
          align-items: center;
          margin-bottom: 0.6rem;
          min-height: 24px;
        }
        
        .chart-label {
          width: ${orientation === 'landscape' ? '100px' : '120px'};
          font-weight: 600;
          font-size: ${orientation === 'landscape' ? '0.75rem' : '0.85rem'};
          word-wrap: break-word;
          overflow-wrap: break-word;
          line-height: 1.2;
        }
        
        .chart-bar-bg {
          flex: 1;
          height: 18px;
          background: ${options.darkBackground ? '#374151' : '#f1f5f9'};
          border-radius: 9px;
          overflow: hidden;
          margin: 0 0.6rem;
          min-width: 60px;
        }
        
        .chart-bar-fill {
          height: 100%;
          background: ${colors.gradient};
          border-radius: 9px;
          transition: width 0.3s ease;
          min-width: 2px;
        }
        
        .chart-value {
          font-weight: bold;
          color: ${colors.primary};
          min-width: ${orientation === 'landscape' ? '50px' : '60px'};
          font-size: ${orientation === 'landscape' ? '0.75rem' : '0.85rem'};
          text-align: right;
        }
        
        .strengths-list, .weaknesses-list {
          list-style: none;
          margin: 0;
          padding: 0;
        }
        
        .strengths-list li, .weaknesses-list li {
          padding: ${orientation === 'landscape' ? '0.3rem 0' : '0.4rem 0'};
          padding-left: ${orientation === 'landscape' ? '1.5rem' : '2rem'};
          position: relative;
          font-size: ${orientation === 'landscape' ? '0.8rem' : '0.9rem'};
          line-height: 1.4;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        
        .strengths-list li:before {
          content: "✓";
          position: absolute;
          left: 0;
          color: ${colors.primary};
          font-weight: bold;
          font-size: ${orientation === 'landscape' ? '1rem' : '1.1rem'};
        }
        
        .weaknesses-list li:before {
          content: "⚠";
          position: absolute;
          left: 0;
          color: #ef4444;
          font-weight: bold;
          font-size: ${orientation === 'landscape' ? '1rem' : '1.1rem'};
        }
        
        .watermark {
          position: absolute;
          top: 15px;
          right: 15px;
          color: #cbd5e1;
          font-size: 0.7rem;
          font-weight: bold;
          z-index: 10;
        }
        
        .footer {
          position: absolute;
          bottom: 15px;
          left: 50%;
          transform: translateX(-50%);
          color: #94a3b8;
          font-size: 0.75rem;
          z-index: 10;
        }
        
        .audience-demographics {
          display: grid;
          grid-template-columns: ${orientation === 'landscape' ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)'};
          gap: ${orientation === 'landscape' ? '0.8rem' : '1rem'};
          margin: 1rem 0;
        }
        
        .demo-card {
          background: white;
          border: 2px solid ${colors.primary}20;
          padding: ${orientation === 'landscape' ? '0.8rem' : '1rem'};
          border-radius: 6px;
          text-align: center;
          page-break-inside: avoid;
        }
        
        .demo-card h4 {
          color: ${colors.primary};
          margin-bottom: 0.4rem;
          font-size: ${orientation === 'landscape' ? '0.9rem' : '1rem'};
          word-wrap: break-word;
        }
        
        .demo-card p {
          font-size: ${orientation === 'landscape' ? '0.75rem' : '0.85rem'};
          line-height: 1.3;
          word-wrap: break-word;
        }

        /* Revenue projections specific styles */
        .stat-card-grid {
          display: grid;
          grid-template-columns: ${orientation === 'landscape' ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)'};
          gap: ${orientation === 'landscape' ? '1rem' : '1.2rem'};
          margin: 1.5rem 0;
        }
        
        .stat-card {
          background: ${colors.gradient};
          color: white;
          padding: ${orientation === 'landscape' ? '1rem' : '1.2rem'};
          border-radius: 8px;
          text-align: center;
          page-break-inside: avoid;
        }
        
        .stat-card .title {
          font-size: ${orientation === 'landscape' ? '0.7rem' : '0.8rem'};
          opacity: 0.9;
          margin-bottom: 0.5rem;
          letter-spacing: 0.5px;
          word-wrap: break-word;
        }
        
        .stat-card .value {
          font-size: ${orientation === 'landscape' ? '1.4rem' : '1.8rem'};
          font-weight: bold;
          word-wrap: break-word;
        }
        
        .ancillary-grid {
          display: grid;
          grid-template-columns: ${orientation === 'landscape' ? 'repeat(2, 1fr)' : '1fr'};
          gap: ${orientation === 'landscape' ? '1rem' : '1.2rem'};
          margin: 1rem 0;
        }
        
        .ancillary-card {
          background: ${options.darkBackground ? '#1e293b' : 'white'};
          border-left: 4px solid ${colors.primary};
          padding: ${orientation === 'landscape' ? '1rem' : '1.2rem'};
          border-radius: 6px;
          page-break-inside: avoid;
        }
        
        .ancillary-card h4 {
          margin-bottom: 0.5rem;
          font-size: ${orientation === 'landscape' ? '0.9rem' : '1rem'};
          word-wrap: break-word;
        }
        
        .ancillary-card .revenue {
          font-size: ${orientation === 'landscape' ? '1.1rem' : '1.3rem'};
          font-weight: bold;
          margin-bottom: 0.5rem;
          word-wrap: break-word;
        }
        
        .ancillary-card .rationale {
          font-size: ${orientation === 'landscape' ? '0.75rem' : '0.85rem'};
          line-height: 1.4;
          color: ${options.darkBackground ? '#d1d5db' : '#64748b'};
          word-wrap: break-word;
          overflow-wrap: break-word;
        }

        /* Single column layout for text-heavy sections */
        .single-column {
          grid-template-columns: 1fr !important;
        }
        
        /* Compact spacing for landscape */
        .landscape-compact .section {
          margin-bottom: 1rem;
        }
        
        .landscape-compact .content-card {
          padding: 0.8rem;
        }
        
        .landscape-compact .highlight-box {
          padding: 1rem;
        }
        
        @media print {
          body { -webkit-print-color-adjust: exact; }
          .page { page-break-inside: avoid; }
          .section { page-break-inside: avoid; }
          .content-card { page-break-inside: avoid; }
          .chart-container { page-break-inside: avoid; }
        }
      </style>
`;
}

const generateModernDossierPDF = async (allData, setNotification, options = {}) => {
  const {
    template = 'professional',
    orientation = 'portrait',
    includeImages = true,
    includeCharts = true,
    includeFinancials = true,
    confidential = true
  } = options;

  if (!allData.filmData) {
    setNotification({ type: 'error', message: 'No project data to export.' });
    return;
  }

  setNotification({ type: 'info', message: `Generating ${template} dossier PDF...` });

  try {
    // Create a hidden container for the PDF content
    const pdfContainer = document.createElement('div');
    pdfContainer.style.cssText = `
      position: fixed;
      top: -9999px;
      left: -9999px;
      width: ${orientation === 'landscape' ? '297mm' : '210mm'};
      background: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;
    document.body.appendChild(pdfContainer);

    // Enhanced color schemes
    const colorSchemes = {
      professional: {
        primary: 'rgb(168, 85, 247)',
        secondary: 'rgb(59, 130, 246)',
        accent: 'rgb(236, 72, 153)',
        gradient: 'linear-gradient(135deg, rgb(168, 85, 247), rgb(59, 130, 246))',
        text: 'rgb(39, 39, 42)'
      },
      creative: {
        primary: 'rgb(236, 72, 153)',
        secondary: 'rgb(139, 92, 246)',
        accent: 'rgb(34, 197, 94)',
        gradient: 'linear-gradient(135deg, rgb(236, 72, 153), rgb(139, 92, 246))',
        text: 'rgb(30, 41, 59)'
      },
      investor: {
        primary: 'rgb(34, 197, 94)',
        secondary: 'rgb(16, 185, 129)',
        accent: 'rgb(59, 130, 246)',
        gradient: 'linear-gradient(135deg, rgb(34, 197, 94), rgb(16, 185, 129))',
        text: 'rgb(17, 24, 39)'
      },
      festival: {
        primary: 'rgb(251, 146, 60)',
        secondary: 'rgb(245, 101, 101)',
        accent: 'rgb(168, 85, 247)',
        gradient: 'linear-gradient(135deg, rgb(251, 146, 60), rgb(245, 101, 101))',
        text: 'rgb(69, 26, 3)'
      }
    };

    const colors = colorSchemes[options.colorTheme || template] || colorSchemes.professional;
    
    const formatCurrency = (value) => {
        if (typeof value !== 'number' || isNaN(value)) return 'N/A';
        if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
        if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
        return `$${value}`;
    };

    // Safe data access helpers
    const safeGet = (obj, path, fallback = '') => {
      try {
        return path.split('.').reduce((o, p) => o?.[p], obj) || fallback;
      } catch {
        return fallback;
      }
    };

    const safeArray = (arr) => Array.isArray(arr) ? arr : [];
    const safeString = (str) => typeof str === 'string' ? str : '';
    const safeNumber = (num) => typeof num === 'number' && !isNaN(num) ? num : 0;

    // Calculate responsive dimensions
    const pageWidth = orientation === 'landscape' ? '297mm' : '210mm';
    const pageHeight = orientation === 'landscape' ? '210mm' : '297mm';
    const contentPadding = orientation === 'landscape' ? '12mm' : '15mm';
    const baseFontSize = orientation === 'landscape' ? '0.85rem' : '1rem';

    // Generate the HTML content with improved formatting
    const htmlContent = `
      ${_dossierPdfStyles(orientation, colors, options, baseFontSize, pageWidth, pageHeight, contentPadding, safeGet(allData, 'posterA') || safeGet(allData, 'posterB') || '')}
      
      <div class="page title-page">
        ${confidential ? `<div class="watermark">CONFIDENTIAL</div>` : ''}
        <div class="page-content">
          <h1>${safeString(safeGet(allData, 'filmData.title', 'FILM TITLE')).toUpperCase()}</h1>
          <div class="subtitle">
            ${template === 'investor' ? 'INVESTMENT PRESENTATION' :
              template === 'creative' ? 'CREATIVE PORTFOLIO' :
              template === 'festival' ? 'FESTIVAL SUBMISSION' :
              'MARKETING DOSSIER'}
          </div>
          ${safeGet(allData, 'filmData.logline') ? `<div class="logline">"${safeString(safeGet(allData, 'filmData.logline'))}"</div>` : ''}

          ${safeGet(allData, 'filmData.stars') || safeGet(allData, 'filmData.director') ? `
          <div style="margin-top: 3rem; display: flex; justify-content: center; gap: ${orientation === 'landscape' ? '2rem' : '3rem'}; flex-wrap: wrap;">
              ${safeGet(allData, 'filmData.stars') ? `
              <div>
                  <p style="font-size: 0.9rem; text-transform: uppercase; letter-spacing: 1px; opacity: 0.8;">Starring</p>
                  <p style="font-size: ${orientation === 'landscape' ? '1rem' : '1.2rem'}; font-weight: 600; word-wrap: break-word; text-align: center;">${safeString(safeGet(allData, 'filmData.stars'))}</p>
              </div>
              ` : ''}
              ${safeGet(allData, 'filmData.director') ? `
              <div>
                  <p style="font-size: 0.9rem; text-transform: uppercase; letter-spacing: 1px; opacity: 0.8;">Directed By</p>
                  <p style="font-size: ${orientation === 'landscape' ? '1rem' : '1.2rem'}; font-weight: 600; word-wrap: break-word; text-align: center;">${safeString(safeGet(allData, 'filmData.director'))}</p>
              </div>
              ` : ''}
          </div>
          ` : ''}
        </div>
        <div class="footer">Generated: ${new Date().toLocaleDateString()}</div>
      </div>
      
      <div class="page ${orientation === 'landscape' ? 'landscape-compact' : ''}">
        ${confidential ? `<div class="watermark">CONFIDENTIAL</div>` : ''}
        <div class="header">
          <h1>AI Health Check</h1>
          <div class="subtitle">Executive Summary & Strategic Assessment</div>
        </div>
        
        <div class="page-content">
          <div class="highlight-box">
            <h3 style="color: ${colors.primary}; margin-bottom: 1rem;">Overall Health Assessment</h3>
            <p style="font-size: ${orientation === 'landscape' ? '1rem' : '1.1rem'}; line-height: 1.6;">
              ${safeString(safeGet(allData, 'filmData.health_check_snapshot.overallHealth', 'The project shows strong foundation elements with compelling narrative potential. Key strengths include solid audience targeting and creative positioning, while primary areas for optimization focus on marketing clarity and buzz generation strategies.'))}
            </p>
          </div>
          
          <div class="content-grid">
            <div class="content-card">
              <h3>💪 Greatest Strength</h3>
              <p>${safeString(safeGet(allData, 'filmData.health_check_snapshot.biggestStrength', 'N/A'))}</p>
            </div>
            <div class="content-card">
              <h3>⚠️ Primary Risk</h3>
              <p>${safeString(safeGet(allData, 'filmData.health_check_snapshot.greatestRisk', 'N/A'))}</p>
            </div>
          </div>
        </div>
      </div>
      
      ${safeArray(safeGet(allData, 'filmData.ai_analyses')).length > 0 ? `
      <div class="page ${orientation === 'landscape' ? 'landscape-compact' : ''}">
        ${confidential ? `<div class="watermark">CONFIDENTIAL</div>` : ''}
        <div class="header">
          <h1>Audience DNA</h1>
          <div class="subtitle">Target Market Analysis & Consumer Insights</div>
        </div>
        
        <div class="page-content">
          <div class="section">
            <div class="section-header">🎯 Target Audience Breakdown</div>
            <div class="audience-demographics">
              <div class="demo-card">
                <h4>Primary Audience</h4>
                <p>${safeGet(allData, `filmData.ai_analyses.${safeArray(safeGet(allData, 'filmData.ai_analyses')).length - 1}.analysis.target_audience_breakdown.primary_audience_profile`, 'N/A')}</p>
              </div>
              <div class="demo-card">
                <h4>Secondary Audience</h4>
                <p>${safeGet(allData, `filmData.ai_analyses.${safeArray(safeGet(allData, 'filmData.ai_analyses')).length - 1}.analysis.target_audience_breakdown.secondary_audience_profile`, 'N/A')}</p>
              </div>
            </div>
            
            <div class="content-card">
              <h3>Key Psychographics</h3>
              <p>${safeArray(safeGet(allData, `filmData.ai_analyses.${safeArray(safeGet(allData, 'filmData.ai_analyses')).length - 1}.analysis.target_audience_breakdown.psychographics`)).join(', ') || 'N/A'}</p>
            </div>
          </div>
          
          <div class="section">
            <div class="section-header">📈 Core Marketing Pillars</div>
            <ul class="strengths-list">
              ${safeArray(safeGet(allData, `filmData.ai_analyses.${safeArray(safeGet(allData, 'filmData.ai_analyses')).length - 1}.analysis.marketing_pillars_strategy.pillars`)).map(p => `<li><strong>${p.pillar}:</strong> ${p.detail}</li>`).join('') || '<li>No marketing pillars defined.</li>'}
            </ul>
          </div>
        </div>
      </div>
      ` : ''}
      
      ${safeGet(allData, 'pressStrategy') ? `
      <div class="page ${orientation === 'landscape' ? 'landscape-compact' : ''}">
        ${confidential ? `<div class="watermark">CONFIDENTIAL</div>` : ''}
        <div class="header">
          <h1>Press & PR Strategy</h1>
          <div class="subtitle">Media Outreach & Narrative Pillars</div>
        </div>
        
        <div class="page-content">
          <div class="section">
            <div class="section-header">Executive Summary</div>
            <div class="content-card">
              <p>${safeString(safeGet(allData, 'pressStrategy.executive_summary', 'N/A'))}</p>
            </div>
          </div>
          
          <div class="section">
            <div class="section-header">Core Strategy Pillars</div>
            <div class="content-grid ${orientation === 'landscape' && safeArray(safeGet(allData, 'pressStrategy.strategy_pillars')).length > 2 ? 'single-column' : ''}">
              ${safeArray(safeGet(allData, 'pressStrategy.strategy_pillars')).map(pillar => `
              <div class="content-card">
                <h3>${safeString(safeGet(pillar, 'pillar', 'Strategy Pillar'))}</h3>
                <p>${safeString(safeGet(pillar, 'description', 'Description not available.'))}</p>
              </div>
              `).join('')}
            </div>
          </div>
        </div>
      </div>
      ` : ''}

      ${safeGet(allData, 'budgetPlan') && includeFinancials ? `
      <div class="page ${orientation === 'landscape' ? 'landscape-compact' : ''}">
        ${confidential ? `<div class="watermark">CONFIDENTIAL</div>` : ''}
        <div class="header">
          <h1>Financial Blueprint</h1>
          <div class="subtitle">Budget Strategy & Resource Allocation</div>
        </div>
        
        <div class="page-content">
          <div class="highlight-box">
            <h3 style="color: ${colors.primary}; margin-bottom: 1rem;">Executive Summary</h3>
            <p>${safeString(safeGet(allData, 'budgetPlan.executive_snapshot.rationale', 'This data-driven plan prioritizes digital marketing to maximize ROI within the budget.'))}</p>
          </div>
          
          ${safeArray(safeGet(allData, 'budgetPlan.budget_allocation_table')).length > 0 && includeCharts ? `
          <div class="section">
            <div class="section-header">💰 Budget Allocation</div>
            <div class="chart-container">
              ${safeArray(safeGet(allData, 'budgetPlan.budget_allocation_table')).map(item => {
                const budgetTable = safeArray(safeGet(allData, 'budgetPlan.budget_allocation_table'));
                const totalBudget = budgetTable.reduce((sum, i) => sum + safeNumber(safeGet(i, 'dollar_amount', 0)), 0);
                const percentage = totalBudget > 0 ? (safeNumber(safeGet(item, 'dollar_amount', 0)) / totalBudget) * 100 : 0;
                return `
                  <div class="chart-bar">
                    <div class="chart-label">${safeString(safeGet(item, 'bucket', 'Budget Item'))}</div>
                    <div class="chart-bar-bg">
                      <div class="chart-bar-fill" style="width: ${percentage}%"></div>
                    </div>
                    <div class="chart-value">${formatCurrency(safeNumber(safeGet(item, 'dollar_amount', 0)))}</div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>
          ` : ''}
          
          ${safeArray(safeGet(allData, 'budgetPlan.executive_snapshot.kpis')).length > 0 ? `
          <div class="section">
            <div class="section-header">📈 Key Performance Indicators</div>
            <ul class="strengths-list">
              ${safeArray(safeGet(allData, 'budgetPlan.executive_snapshot.kpis')).slice(0, orientation === 'landscape' ? 4 : 6).map(kpi => `<li>${safeString(kpi)}</li>`).join('')}
            </ul>
          </div>
          ` : ''}
        </div>
      </div>
      ` : ''}
      
      ${safeGet(allData, 'revenueForecast') && includeFinancials ? `
      <div class="page ${orientation === 'landscape' ? 'landscape-compact' : ''}">
        ${confidential ? `<div class="watermark">CONFIDENTIAL</div>` : ''}
        <div class="header">
          <h1>Revenue Potential</h1>
          <div class="subtitle">Financial Projections & Market Forecasts</div>
        </div>
        
        <div class="page-content">
          ${safeGet(allData, 'revenueForecast.type') === 'theatrical' ? `
          <div class="section">
            <div class="section-header">🎬 Theatrical Projections</div>
            <div class="stat-card-grid">
              <div class="stat-card">
                <div class="title">OPENING WEEKEND</div>
                <div class="value">${formatCurrency(safeNumber(safeGet(allData, 'revenueForecast.data.theatrical_release_projections.opening_weekend_forecast')))}</div>
              </div>
              <div class="stat-card">
                <div class="title">DOMESTIC BOX OFFICE</div>
                <div class="value">${formatCurrency(safeNumber(safeGet(allData, 'revenueForecast.data.theatrical_release_projections.domestic_box_office_forecast')))}</div>
              </div>
              <div class="stat-card">
                <div class="title">INTERNATIONAL BOX OFFICE</div>
                <div class="value">${formatCurrency(safeNumber(safeGet(allData, 'revenueForecast.data.theatrical_release_projections.international_box_office_forecast')))}</div>
              </div>
            </div>
            
            <div class="content-grid">
              <div class="content-card">
                  <h3>Strategic Analysis</h3>
                  <h4>Underserved Opportunities</h4>
                  <p>${safeString(safeGet(allData, 'revenueForecast.data.underserved_opportunities', 'N/A'))}</p>
                  <h4 style="margin-top: 1rem;">Revenue Split Analysis</h4>
                  <p>${safeString(safeGet(allData, 'revenueForecast.data.theatrical_release_projections.revenue_split_analysis', 'N/A'))}</p>
              </div>
              <div class="content-card">
                  <h3>Competitor Snapshot</h3>
                  ${safeArray(safeGet(allData, 'revenueForecast.data.competitor_analysis')).slice(0, 2).map(comp => `
                      <div style="margin-bottom: 1rem;">
                          <h4 style="margin-bottom: 0.3rem;">${safeString(safeGet(comp, 'title', 'Competitor'))}</h4>
                          <p style="font-size: 0.85rem;">${safeString(safeGet(comp, 'analysis', 'Analysis not available.'))}</p>
                      </div>
                  `).join('') || '<p>No competitor data available.</p>'}
              </div>
            </div>
          </div>
          <div class="page">
             <div class="section-header">💰 Ancillary Revenue Streams</div>
              <div class="ancillary-grid">
                  <div class="ancillary-card" style="border-left: 4px solid #a855f7;">
                      <h4>Home Entertainment</h4>
                      <div class="revenue" style="color: #a855f7;">${safeString(safeGet(allData, 'revenueForecast.data.ancillary_revenue_streams.home_entertainment.estimated_revenue', 'N/A'))}</div>
                      <p class="rationale">${safeString(safeGet(allData, 'revenueForecast.data.ancillary_revenue_streams.home_entertainment.rationale', ''))}</p>
                  </div>
                  <div class="ancillary-card" style="border-left: 4px solid #38bdf8;">
                      <h4>Streaming & Broadcast</h4>
                      <div class="revenue" style="color: #38bdf8;">${safeString(safeGet(allData, 'revenueForecast.data.ancillary_revenue_streams.streaming_and_broadcast.estimated_revenue', 'N/A'))}</div>
                      <p class="rationale">${safeString(safeGet(allData, 'revenueForecast.data.ancillary_revenue_streams.streaming_and_broadcast.rationale', ''))}</p>
                  </div>
                   <div class="ancillary-card" style="border-left: 4px solid #4ade80;">
                      <h4>Merchandising & Licensing</h4>
                      <div class="revenue" style="color: #4ade80;">${safeString(safeGet(allData, 'revenueForecast.data.ancillary_revenue_streams.merchandising_and_licensing.estimated_revenue', 'N/A'))}</div>
                      <p class="rationale">${safeString(safeGet(allData, 'revenueForecast.data.ancillary_revenue_streams.merchandising_and_licensing.rationale', ''))}</p>
                  </div>
                  <div class="ancillary-card" style="border-left: 4px solid #facc15;">
                      <h4>Other Non-Theatrical</h4>
                      <div class="revenue" style="color: #facc15;">${safeString(safeGet(allData, 'revenueForecast.data.ancillary_revenue_streams.other_non_theatrical.estimated_revenue', 'N/A'))}</div>
                      <p class="rationale">${safeString(safeGet(allData, 'revenueForecast.data.ancillary_revenue_streams.other_non_theatrical.rationale', ''))}</p>
                  </div>
              </div>
          </div>
          ` : `
          <div class="section">
            <div class="section-header">💻 Streaming Projections</div>
            <div class="ancillary-grid">
              <div class="ancillary-card" style="border-left: 4px solid #a855f7;">
                  <h4>SVOD (Subscription)</h4>
                  <div class="revenue" style="color: #a855f7;">${safeString(safeGet(allData, 'revenueForecast.data.svod_forecast.estimated_revenue', 'N/A'))}</div>
                  <p class="rationale">${safeString(safeGet(allData, 'revenueForecast.data.svod_forecast.rationale', ''))}</p>
              </div>
              <div class="ancillary-card" style="border-left: 4px solid #38bdf8;">
                  <h4>AVOD (Ad-Supported)</h4>
                  <div class="revenue" style="color: #38bdf8;">${safeString(safeGet(allData, 'revenueForecast.data.avod_forecast.estimated_revenue', 'N/A'))}</div>
                  <p class="rationale">${safeString(safeGet(allData, 'revenueForecast.data.avod_forecast.rationale', ''))}</p>
              </div>
            </div>
          </div>
          `}
        </div>
      </div>
      ` : ''}
    `;

    pdfContainer.innerHTML = htmlContent;

    // Use modern browser print API for better quality
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${safeString(safeGet(allData, 'filmData.title', 'Marketing'))} ${template.charAt(0).toUpperCase() + template.slice(1)} Dossier</title>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
        </head>
        <body>
          ${htmlContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    // Wait for content to load then trigger print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 1500);

    // Clean up
    document.body.removeChild(pdfContainer);

    setNotification({
      type: 'success',
      message: `${template.charAt(0).toUpperCase() + template.slice(1)} dossier ready for download!`
    });

  } catch (error) {
    setNotification({
      type: 'error',
      message: `PDF generation failed: ${error.message}`
    });
    console.error('Modern PDF Error:', error);
  }
};

function _printDossierDocument(docTitle, htmlContent) {
    // Same hidden-container + browser-print-dialog approach generateModernDossierPDF
    // uses — kept as its own small helper here rather than reworking that function,
    // since these two new exports don't need any of its template-picker machinery.
    const container = document.createElement('div');
    container.style.cssText = `position: fixed; top: -9999px; left: -9999px;`;
    document.body.appendChild(container);
    container.innerHTML = htmlContent;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${docTitle}</title>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
        </head>
        <body>${htmlContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
        printWindow.print();
        printWindow.close();
    }, 1500);

    document.body.removeChild(container);
}

// ═══════════════════════════════════════════════════════════════════════════
// SOCIAL PLAN EXPORT — everything Generate Campaign produced (Audience Genius
// creative matrix + Story & Hooks + Social Playbook), as its own standalone
// document. Deliberately excludes press, financials, greenlight, and
// competitive intel — those belong to the PR Plan export or the Appendix.
// ═══════════════════════════════════════════════════════════════════════════
const generateSocialPlanPDF = async (allData, setNotification) => {
    const { filmData } = allData;
    if (!filmData) {
        setNotification({ type: 'error', message: 'No project data to export.' });
        return;
    }

    const audienceAnalysis = filmData.ai_analyses?.[filmData.ai_analyses.length - 1]?.analysis || null;
    const scriptAnalysis = filmData.script_analysis || null;
    const socialPlaybook = filmData.social_playbook || null;

    if (!audienceAnalysis && !scriptAnalysis && !socialPlaybook) {
        setNotification({ type: 'error', message: 'Nothing to export yet — run Generate Campaign first.' });
        return;
    }

    setNotification({ type: 'info', message: 'Generating Social Plan PDF...' });

    try {
        const safeGet = (obj, path, fallback = '') => {
            try { return path.split('.').reduce((o, p) => o?.[p], obj) ?? fallback; } catch { return fallback; }
        };
        const safeArray = (arr) => Array.isArray(arr) ? arr : [];
        const safeString = (str) => typeof str === 'string' ? str : '';

        const colors = { primary: 'rgb(245, 158, 11)', secondary: 'rgb(168, 85, 247)', accent: 'rgb(236, 72, 153)', text: 'rgb(39, 39, 42)' };
        const orientation = 'portrait';
        const options = { darkBackground: false, confidential: true };
        const baseFontSize = '1rem';
        const pageWidth = '210mm';
        const pageHeight = '297mm';
        const contentPadding = '15mm';

        const matrix = safeArray(audienceAnalysis?.creative_matrix);
        const hooks = scriptAnalysis?.campaign_hooks;
        const quotes = safeArray(scriptAnalysis?.quotable_dialogue).slice(0, 5);
        const timeline = safeArray(socialPlaybook?.release_timeline);
        const characterAngles = safeArray(socialPlaybook?.character_social_angles);
        const owp = socialPlaybook?.opening_weekend_plan;
        const paidStrategy = socialPlaybook?.paid_promotion_strategy;
        const maintenance = socialPlaybook?.maintenance_schedule;

        const htmlContent = `
      ${_dossierPdfStyles(orientation, colors, options, baseFontSize, pageWidth, pageHeight, contentPadding, safeGet(allData, 'posterA') || safeGet(allData, 'posterB') || '')}

      <div class="page title-page">
        ${options.confidential ? `<div class="watermark">CONFIDENTIAL</div>` : ''}
        <div class="page-content">
          <h1>${safeString(safeGet(filmData, 'title', 'FILM TITLE')).toUpperCase()}</h1>
          <div class="subtitle">SOCIAL MEDIA PLAN</div>
          <p style="margin-top:20px;">${safeString(filmData.logline)}</p>
        </div>
      </div>

      ${matrix.length ? `
      <div class="page">
        <div class="header"><h1>Andromeda Creative Matrix</h1><div class="subtitle">The hooks every asset in this plan is built around</div></div>
        <div class="page-content">
          ${matrix.map(m => `
            <div class="content-card" style="margin-bottom:16px;">
              <h3>${safeString(m.hook_name)}</h3>
              <p><strong>Trigger:</strong> ${safeString(m.psychological_trigger)}</p>
              <p><strong>Visual direction:</strong> ${safeString(m.visual_direction)}</p>
              <p><strong>Copy angle:</strong> ${safeString(m.copy_angle)}</p>
            </div>
          `).join('')}
        </div>
      </div>` : ''}

      ${hooks ? `
      <div class="page">
        <div class="header"><h1>Campaign Hooks</h1><div class="subtitle">From Story &amp; Hooks</div></div>
        <div class="page-content">
          <div class="content-card"><h3>Primary Hook</h3><p>${safeString(hooks.primary_hook)}</p></div>
          ${safeArray(hooks.secondary_hooks).length ? `<div class="content-card"><h3>Secondary Hooks</h3><ul>${safeArray(hooks.secondary_hooks).map(h => `<li>${safeString(h)}</li>`).join('')}</ul></div>` : ''}
          ${safeArray(hooks.tagline_suggestions).length ? `<div class="content-card"><h3>Taglines</h3><ul>${safeArray(hooks.tagline_suggestions).map(t => `<li>${safeString(t)}</li>`).join('')}</ul></div>` : ''}
          ${quotes.length ? `<div class="content-card"><h3>Quotable Dialogue</h3><ul>${quotes.map(q => `<li>"${safeString(q.quote)}" — ${safeString(q.character)}</li>`).join('')}</ul></div>` : ''}
        </div>
      </div>` : ''}

      ${timeline.length || characterAngles.length ? `
      <div class="page">
        <div class="header"><h1>Social Playbook</h1><div class="subtitle">Release timeline &amp; character angles</div></div>
        <div class="page-content">
          ${timeline.map(entry => `
            <div class="content-card" style="margin-bottom:12px;">
              <h3>${safeString(entry.phase)}</h3>
              <p>${safeString(entry.strategic_goal)}</p>
              <p style="opacity:0.7;">${safeArray(entry.posts).length} post(s) planned</p>
            </div>
          `).join('')}
          ${characterAngles.length ? `
            <h3 style="margin-top:20px;">Character Social Angles</h3>
            ${characterAngles.map(c => `
              <div class="content-card" style="margin-bottom:12px;">
                <h4>${safeString(c.character_name)} — ${safeString(c.social_angle)}</h4>
                <p><strong>Signature moment:</strong> ${safeString(c.signature_moment)}</p>
                <p><strong>Serves hook:</strong> ${safeString(c.andromeda_hook_served)}</p>
              </div>
            `).join('')}
          ` : ''}
        </div>
      </div>` : ''}

      ${owp || paidStrategy || maintenance ? `
      <div class="page">
        <div class="header"><h1>Launch &amp; Paid Strategy</h1><div class="subtitle">Opening weekend, paid spend, maintenance</div></div>
        <div class="page-content">
          ${owp ? `
            <div class="content-card"><h3>Opening Weekend War Room</h3>
              <p><strong>Thursday preview:</strong> ${safeString(owp.thursday_preview)}</p>
              <p><strong>Saturday push:</strong> ${safeString(owp.saturday_push)}</p>
              <p><strong>Sunday close:</strong> ${safeString(owp.sunday_close)}</p>
            </div>` : ''}
          ${paidStrategy ? `
            <div class="content-card"><h3>Paid Promotion Strategy</h3>
              <p>${safeString(paidStrategy.philosophy)}</p>
              <p><strong>Sequencing:</strong> ${safeString(paidStrategy.budget_sequencing)}</p>
              ${safeArray(paidStrategy.top_paid_drops).length ? `<ul>${safeArray(paidStrategy.top_paid_drops).map(d => `<li>#${d.rank} ${safeString(d.moment)} — ${safeString(d.suggested_daily_budget)}/day</li>`).join('')}</ul>` : ''}
            </div>` : ''}
          ${maintenance ? `
            <div class="content-card"><h3>Maintenance Schedule</h3>
              <p>${safeString(maintenance.monthly_cadence)}</p>
            </div>` : ''}
        </div>
      </div>` : ''}
    `;

        _printDossierDocument(`${safeString(safeGet(filmData, 'title', 'Untitled'))} Social Plan`, htmlContent);
        setNotification({ type: 'success', message: 'Social Plan ready for download!' });
    } catch (error) {
        setNotification({ type: 'error', message: `Export failed: ${error.message}` });
        console.error('Social Plan PDF Error:', error);
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// PR PLAN EXPORT — Press & Outreach's strategy, target outlets, and timeline
// as its own standalone document. No financials, no greenlight verdict, no
// competitive intel folded in — those live in the Appendix export instead.
// ═══════════════════════════════════════════════════════════════════════════
const generatePRPlanPDF = async (allData, setNotification) => {
    const { filmData, pressStrategy } = allData;
    if (!pressStrategy) {
        setNotification({ type: 'error', message: 'No Press Strategy found — generate one in Press & Outreach first.' });
        return;
    }

    setNotification({ type: 'info', message: 'Generating PR Plan PDF...' });

    try {
        const safeGet = (obj, path, fallback = '') => {
            try { return path.split('.').reduce((o, p) => o?.[p], obj) ?? fallback; } catch { return fallback; }
        };
        const safeArray = (arr) => Array.isArray(arr) ? arr : [];
        const safeString = (str) => typeof str === 'string' ? str : '';

        const colors = { primary: 'rgb(168, 85, 247)', secondary: 'rgb(59, 130, 246)', accent: 'rgb(236, 72, 153)', text: 'rgb(39, 39, 42)' };
        const orientation = 'portrait';
        const options = { darkBackground: false, confidential: true };
        const baseFontSize = '1rem';
        const pageWidth = '210mm';
        const pageHeight = '297mm';
        const contentPadding = '15mm';

        const outlets = pressStrategy.target_outlets;
        const timeline = safeArray(pressStrategy.timeline);

        const htmlContent = `
      ${_dossierPdfStyles(orientation, colors, options, baseFontSize, pageWidth, pageHeight, contentPadding, safeGet(allData, 'posterA') || safeGet(allData, 'posterB') || '')}

      <div class="page title-page">
        ${options.confidential ? `<div class="watermark">CONFIDENTIAL</div>` : ''}
        <div class="page-content">
          <h1>${safeString(safeGet(filmData, 'title', 'FILM TITLE')).toUpperCase()}</h1>
          <div class="subtitle">PR PLAN</div>
        </div>
      </div>

      <div class="page">
        <div class="header"><h1>Press &amp; PR Strategy</h1><div class="subtitle">Media outreach &amp; narrative pillars</div></div>
        <div class="page-content">
          <div class="content-card"><h3>Executive Summary</h3><p>${safeString(pressStrategy.executive_summary)}</p></div>
          ${safeArray(pressStrategy.strategy_pillars).length ? `
            <h3 style="margin-top:16px;">Strategy Pillars</h3>
            ${safeArray(pressStrategy.strategy_pillars).map(p => `
              <div class="content-card" style="margin-bottom:12px;"><h4>${safeString(p.pillar)}</h4><p>${safeString(p.description)}</p></div>
            `).join('')}
          ` : ''}
        </div>
      </div>

      ${outlets ? `
      <div class="page">
        <div class="header"><h1>Target Outlets</h1></div>
        <div class="page-content">
          ${['tier_1', 'tier_2', 'niche_specialized'].map(tier => safeArray(outlets[tier]).length ? `
            <h3 style="margin-top:12px;">${tier === 'tier_1' ? 'Tier 1 — Major Publications' : tier === 'tier_2' ? 'Tier 2 — Industry & Regional' : 'Niche & Specialized'}</h3>
            ${safeArray(outlets[tier]).map(o => `<div class="content-card" style="margin-bottom:8px;"><strong>${safeString(o.outlet)}</strong> (${safeString(o.priority)}) — ${safeString(o.angle)}</div>`).join('')}
          ` : '').join('')}
        </div>
      </div>` : ''}

      ${timeline.length ? `
      <div class="page">
        <div class="header"><h1>Campaign Timeline</h1></div>
        <div class="page-content">
          ${timeline.map(phase => `
            <div class="content-card" style="margin-bottom:12px;">
              <h3>${safeString(phase.phase)}</h3>
              ${safeArray(phase.activities).length ? `<p><strong>Activities:</strong> ${safeArray(phase.activities).join(', ')}</p>` : ''}
              ${safeArray(phase.deliverables).length ? `<p><strong>Deliverables:</strong> ${safeArray(phase.deliverables).join(', ')}</p>` : ''}
            </div>
          `).join('')}
        </div>
      </div>` : ''}
    `;

        _printDossierDocument(`${safeString(safeGet(filmData, 'title', 'Untitled'))} PR Plan`, htmlContent);
        setNotification({ type: 'success', message: 'PR Plan ready for download!' });
    } catch (error) {
        setNotification({ type: 'error', message: `Export failed: ${error.message}` });
        console.error('PR Plan PDF Error:', error);
    }
};

// 2. UPDATED PDF EXPORT MODAL
const PdfExportModal = ({ isOpen, onClose, allData, setNotification, isExporting, setIsExporting }) => {
  const [exportOptions, setExportOptions] = useState({
    template: 'professional',
    orientation: 'portrait',
    includeImages: true,
    includeCharts: true,
    includeFinancials: true,
    confidential: true,
    darkBackground: false,
    colorTheme: 'professional'
  });

  if (!isOpen) return null;

  const templates = [
    {
      id: 'professional',
      name: 'Professional Executive',
      description: 'Clean, corporate design for industry professionals',
      preview: '📊 Executive summary focus with charts and metrics',
      color: 'purple'
    },
    {
      id: 'creative',
      name: 'Creative Showcase', 
      description: 'Visual-heavy layout emphasizing creative assets',
      preview: '🎨 Large images with artistic layout and bold typography',
      color: 'pink'
    },
    {
      id: 'investor',
      name: 'Investor Presentation',
      description: 'Financial-focused with detailed projections',
      preview: '💰 Revenue forecasts, budget breakdowns, ROI analysis',
      color: 'green'
    },
    {
      id: 'festival',
      name: 'Festival Submission',
      description: 'Artistic presentation for film festival submissions',
      preview: '🏆 Creative storytelling with artistic elements',
      color: 'orange'
    }
  ];

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await generateModernDossierPDF(allData, setNotification, exportOptions);
    } catch (error) {
      setNotification({ type: 'error', message: `Export failed: ${error.message}` });
    } finally {
      setIsExporting(false);
    }
    onClose();
  };

  const updateOption = (key, value) => {
    setExportOptions(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-white font-poppins">
            Export Professional Dossier
          </h3>
          <button 
            onClick={onClose} 
            className="text-zinc-400 hover:text-white transition-colors text-xl"
            disabled={isExporting}
          >
            ✕
          </button>
        </div>

        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-6">
          <div className="text-blue-200 text-sm">
            <p className="font-semibold mb-2">🆕 Enhanced PDF System</p>
            <p>• <strong>Modern HTML-based generation</strong> - Matches your dossier's beautiful design</p>
            <p>• <strong>High-quality typography</strong> - Professional fonts and layouts</p>
            <p>• <strong>Rich visual elements</strong> - Gradients, charts, and styled content</p>
            <p>• <strong>Template-specific styling</strong> - Each template has unique visual identity</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Template Selection */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-white">
              📋 Choose Template
            </h4>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {templates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => updateOption('template', template.id)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                    exportOptions.template === template.id
                      ? `border-${template.color}-500 bg-${template.color}-500/10`
                      : 'border-zinc-600 bg-zinc-800/50 hover:border-zinc-500'
                  }`}
                >
                  <div className="text-sm font-semibold text-white mb-1">
                    {template.name}
                  </div>
                  <div className="text-xs text-zinc-300 mb-2">
                    {template.description}
                  </div>
                  <div className="text-xs text-zinc-400">
                    {template.preview}
                  </div>
                </div>
              ))}
            </div>

            {/* Color Theme Selection */}
            <h4 className="text-lg font-semibold mb-4 text-white">
              🎨 Color Theme
            </h4>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
              {[
                { id: 'professional', name: 'Professional', color: 'bg-purple-500', desc: 'Corporate purple' },
                { id: 'creative', name: 'Creative', color: 'bg-pink-500', desc: 'Artistic pink' },
                { id: 'investor', name: 'Investor', color: 'bg-green-500', desc: 'Finance green' },
                { id: 'festival', name: 'Festival', color: 'bg-orange-500', desc: 'Warm orange' },
                { id: 'noir', name: 'Film Noir', color: 'bg-gray-400', desc: 'Classic silver' },
                { id: 'thriller', name: 'Thriller', color: 'bg-red-500', desc: 'Intense red' },
                { id: 'drama', name: 'Drama', color: 'bg-amber-700', desc: 'Rich brown' },
                { id: 'scifi', name: 'Sci-Fi', color: 'bg-cyan-500', desc: 'Futuristic cyan' },
                { id: 'horror', name: 'Horror', color: 'bg-red-800', desc: 'Dark crimson' },
                { id: 'romance', name: 'Romance', color: 'bg-rose-500', desc: 'Romantic rose' }
              ].map((theme) => (
                <button
                  key={theme.id}
                  onClick={() => updateOption('colorTheme', theme.id)}
                  className={`p-2 rounded-lg text-xs transition-all duration-200 ${
                    exportOptions.colorTheme === theme.id
                      ? 'ring-2 ring-white bg-zinc-700'
                      : 'bg-zinc-800 hover:bg-zinc-700'
                  }`}
                >
                  <div className={`w-full h-6 ${theme.color} rounded mb-1`}></div>
                  <div className="text-white font-semibold">{theme.name}</div>
                  <div className="text-zinc-400 text-xs">{theme.desc}</div>
                </button>
              ))}
            </div>

            {/* Format Options */}
            <h4 className="text-lg font-semibold mb-4 text-white">
              📐 Format Options
            </h4>
            
            <div className="space-y-4 mb-6">
              {/* Orientation */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Page Orientation
                </label>
                <div className="flex gap-3">
                  {['portrait', 'landscape'].map((orientation) => (
                    <button
                      key={orientation}
                      onClick={() => updateOption('orientation', orientation)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        exportOptions.orientation === orientation
                          ? 'bg-purple-600 text-white'
                          : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                      }`}
                    >
                      📄 {orientation.charAt(0).toUpperCase() + orientation.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Content Options & Preview */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-white">
              🎛️ Content Inclusions
            </h4>
            
            <div className="space-y-3 mb-6">
              {[
                { key: 'includeImages', label: 'Visual Assets & Posters', icon: '🖼️' },
                { key: 'includeCharts', label: 'Charts & Data Visualizations', icon: '📊' },
                { key: 'includeFinancials', label: 'Budget & Revenue Projections', icon: '💰' },
                { key: 'confidential', label: 'Confidential Watermarks', icon: '🔒' },
                { key: 'darkBackground', label: 'Dark Background Theme', icon: '🌙' }
              ].map((option) => (
                <label key={option.key} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={exportOptions[option.key]}
                    onChange={(e) => updateOption(option.key, e.target.checked)}
                    className="w-4 h-4 text-purple-600 bg-zinc-700 border-zinc-600 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm text-zinc-300">
                    {option.icon} {option.label}
                  </span>
                </label>
              ))}
            </div>

            {/* Project Preview */}
            <div className="bg-zinc-800/50 rounded-lg p-4 mb-6">
              <h5 className="text-sm font-semibold text-white mb-3">📋 Document Preview</h5>
              <div className="text-xs text-zinc-300 space-y-1">
                <p><strong>Project:</strong> {allData?.filmData?.title || 'Untitled'}</p>
                <p><strong>Template:</strong> {templates.find(t => t.id === exportOptions.template)?.name}</p>
                <p><strong>Format:</strong> {exportOptions.orientation} • A4 size</p>
                <p><strong>Pages:</strong> ~{6 + (exportOptions.includeImages ? 2 : 0) + (exportOptions.includeCharts ? 1 : 0)} pages</p>
              </div>
              
              <div className="mt-3 pt-3 border-t border-zinc-700">
                <p className="text-xs text-zinc-400 mb-2"><strong>Sections included:</strong></p>
                <div className="text-xs text-zinc-400 space-y-1">
                  {[
                    '✓ Title Page with Background Image',
                    '✓ mi Health Check & Executive Summary',
                    allData?.filmData?.ai_analyses?.length > 0 ? '✓ Audience DNA & Market Analysis' : null,
                    allData?.visualAnalysis && exportOptions.includeImages ? '✓ Visual Identity Strategy' : null,
                    allData?.videoAnalysis ? '✓ Trailer Performance Analysis' : null,
                    allData?.budgetPlan && exportOptions.includeFinancials ? '✓ Budget Strategy & Allocation' : null,
                    allData?.revenueForecast && exportOptions.includeFinancials ? '✓ Revenue Projections' : null
                  ].filter(Boolean).map((section, idx) => (
                    <p key={idx}>{section}</p>
                  ))}
                </div>
              </div>
            </div>

            {/* Quality Notice */}
            <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3">
              <div className="text-xs text-purple-200">
                <p className="font-semibold mb-1">🚀 Studio-Quality Output</p>
                <p>• Beautiful typography and modern design</p>
                <p>• Template-specific color schemes and layouts</p>
                <p>• High-resolution graphics and professional formatting</p>
                <p>• Print-ready quality matching your web dossier</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-zinc-700">
          <div className="text-sm text-zinc-400">
            {exportOptions.template === 'professional' && '📊 Clean corporate design with data focus'}
            {exportOptions.template === 'creative' && '🎨 Artistic layout with visual emphasis'}
            {exportOptions.template === 'investor' && '💰 Financial-focused business presentation'}
            {exportOptions.template === 'festival' && '🏆 Creative storytelling showcase format'}
          </div>
          
          <div className="flex gap-4">
            <button 
              onClick={onClose} 
              className="px-6 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors"
              disabled={isExporting}
            >
              Cancel
            </button>
            <button 
              onClick={handleExport} 
              className="px-6 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors flex items-center gap-2 font-semibold"
              disabled={isExporting}
            >
              {isExporting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Generating {templates.find(t => t.id === exportOptions.template)?.name}...
                </>
              ) : (
                <>
                  📄 Export {templates.find(t => t.id === exportOptions.template)?.name}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 3. UPDATED INTRO SECTION COMPONENT (Original version used in provided code block, sticking to original for this task)
// const UpdatedIntroSection = ({ /* ...props */ }) => { /* ... */ };


const ContactSection = ({ filmData }) => {
    const [ref, isInView] = useInView();
    return (
        <section id="contact" ref={ref} className="min-h-screen bg-black p-8 md:p-16 flex items-center justify-center text-white">
            <div className={`w-full max-w-4xl mx-auto text-center transition-all duration-1000 ease-out ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <Handshake size={64} className="mx-auto text-purple-400 mb-6" />
                <h2 className="text-5xl font-semibold font-poppins mb-6">The Next Chapter</h2>
                <p className="text-lg text-zinc-300 max-w-3xl mx-auto mb-12">
                    This dossier represents a data-driven foundation for a successful launch. We are seeking passionate partners to bring this vision to the widest possible audience. Let's create something unforgettable together.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto mb-16">
                    <div className="bg-zinc-900 p-6 rounded-xl border border-zinc-800">
                        <h3 className="text-sm uppercase tracking-widest text-zinc-400">General Inquiries</h3>
                        {/* UPDATED: Uses new contactName field */}
                        <p className="text-xl font-semibold mt-1">{filmData.contactName || filmData.director || 'The Filmmaking Team'}</p>
                        {/* UPDATED: Uses new contactEmail field */}
                        <a href={`mailto:${filmData.contactEmail || 'inquiries@example.com'}`} className="text-purple-400 hover:text-purple-300 transition-colors mt-2 inline-block">
                            {filmData.contactEmail || 'inquiries@example.com'}
                        </a>
                    </div>
                </div>

                <div className="flex justify-center items-center gap-2 mt-24 opacity-60">
                    <BlackboxIcon className="w-6 h-6" />
                    <p className="font-semibold">Powered by BLACKBOXmi</p>
                </div>
            </div>
        </section>
    );
};


// --- MAIN COMPONENT ---
const DossierTab = ({ allData, setNotification, isPublicView = false, saveFilmData }) => {
    const { 
        filmData, 
        visualAnalysis, 
        posterA, 
        posterB, 
        videoAnalysis, 
        budgetPlan, 
        revenueForecast, 
        campaigns 
    } = allData;
    
    // Currency formatting utility
    const formatCurrency = (value) => {
        if (typeof value !== 'number' || isNaN(value)) return 'N/A';
        if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
        if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
        return `$${value}`;
    };
    const [selectedPoster, setSelectedPoster] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
    const dossierRef = useRef(null);
    const introSectionRef = useRef(null);
    const [isMenuVisible, setIsMenuVisible] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isPrintEnabled, setIsPrintEnabled] = useState(filmData?.is_dossier_public_print || false);

    // NEW: Active Section Tracker for Navigation
    const [activeSection, setActiveSection] = useState('intro');

    useEffect(() => {
        const aiRecommendedPoster = visualAnalysis?.recommendation === 'Version B' ? posterB : posterA;
        setSelectedPoster(aiRecommendedPoster || posterA || posterB || null);
    }, [posterA, posterB, visualAnalysis]);
    
    // Scroll listener to update active section dot
    useEffect(() => {
        const handleScroll = () => {
            const sections = ['intro', 'health', 'audience', 'visuals', 'trailer', 'press', 'budget', 'timeline', 'revenue', 'campaigns', 'contact'];
            // Find the section closest to the top of the viewport
            for (const section of sections) {
                const element = document.getElementById(section);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    // Check if the section top is within +/- 300px of the viewport top
                    if (rect.top >= -300 && rect.top < 300) { 
                        setActiveSection(section);
                        break;
                    }
                }
            }
        };
        const container = dossierRef.current;
        if (container) container.addEventListener('scroll', handleScroll);
        return () => container && container.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
        setActiveSection(id); // Optimistic update
    };


    const handleFullscreenToggle = () => {
        if (!document.fullscreenElement) {
            dossierRef.current?.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    };

    // Share Modal Component (Internal)
    const ShareDossierModal = () => {
        const { filmData } = allData;
        const isPublic = filmData?.is_dossier_public || false;
        // Construct the link dynamically based on the current window
        const shareUrl = `${window.location.origin}/share/${filmData.id}`;

        const togglePublic = async () => {
            const newValue = !isPublic;
            // Use the existing save function passed from App.js
            if (saveFilmData && filmData) {
                await saveFilmData({ ...filmData, is_dossier_public: newValue });
            }
            
            setNotification({ 
                type: newValue ? 'success' : 'info', 
                message: newValue ? 'Dossier is now public.' : 'Dossier is now private.' 
            });
        };

        const copyLink = () => {
            navigator.clipboard.writeText(shareUrl);
            setNotification({ type: 'success', message: 'Link copied to clipboard!' });
        };

        return (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <AppCard className="p-8 max-w-lg w-full">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-xl font-bold text-white font-poppins">Share Dossier</h3>
                        <button onClick={() => setIsShareModalOpen(false)} className="text-zinc-400 hover:text-white">
                            <X size={24} />
                        </button>
                    </div>

                    <div className="space-y-6">
                        {/* Public Toggle Switch */}
                        <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                            <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-full ${isPublic ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700/50 text-zinc-400'}`}>
                                    {isPublic ? <Globe size={20} /> : <Lock size={20} />}
                                </div>
                                <div>
                                    <p className="font-bold text-white">Public Access</p>
                                    <p className="text-xs text-zinc-400">{isPublic ? 'Anyone with the link can view' : 'Only you can view'}</p>
                                </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" checked={isPublic} onChange={togglePublic} className="sr-only peer" />
                                <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                            </label>
                        </div>

                        {/* Link Display (Only if Public) */}
                        {isPublic && (
                            <div className="space-y-2 animate-fade-in">
                                <label className="text-sm font-semibold text-zinc-300">Public Link</label>
                                <div className="flex gap-2">
                                    <div className="flex-grow bg-black/50 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-300 text-sm truncate font-mono">
                                        {shareUrl}
                                    </div>
                                    <AppButton onClick={copyLink} variant="secondary" icon={Copy} className="flex-shrink-0" title="Copy Link" />
                                    <a href={shareUrl} target="_blank" rel="noopener noreferrer">
                                        <AppButton variant="secondary" icon={ExternalLink} className="flex-shrink-0" title="Open Link" />
                                    </a>
                                </div>
                            </div>
                        )}

                        <div className="pt-4 border-t border-zinc-700 flex justify-end">
                            <AppButton onClick={() => setIsShareModalOpen(false)}>Done</AppButton>
                        </div>
                    </div>
                </AppCard>
            </div>
        );
    };

    return (
        <div className={`bg-zinc-950 text-white font-poppins ${isPublicView ? '' : 'h-full overflow-y-auto'}`} ref={dossierRef}>
            {isShareModalOpen && !isPublicView && <ShareDossierModal />}
            <PdfExportModal 
                isOpen={isPdfModalOpen} 
                onClose={() => setIsPdfModalOpen(false)} 
                allData={allData} 
                setNotification={setNotification} 
                isExporting={isExporting} 
                setIsExporting={setIsExporting} 
            />
            
            {/* NEW: Floating Navigation */}
            {!isPublicView && !isFullscreen && <ChapterNavigation activeSection={activeSection} scrollToSection={scrollToSection} />}


            <IntroSection 
                ref={introSectionRef}
                filmData={filmData} 
                selectedPoster={selectedPoster} 
                isPublicView={isPublicView}
                onShare={() => setIsShareModalOpen(true)}
                onExport={() => setIsPdfModalOpen(true)}
                isExporting={isExporting}
                isPrintEnabled={isPrintEnabled}
                allData={allData}
                setNotification={setNotification}
                isFullscreen={isFullscreen}
            />

            {!isPublicView && (
                <div className={`sticky top-0 z-20 bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800 p-4 transition-opacity ${!isMenuVisible || isFullscreen ? 'opacity-0 pointer-events-none' : ''}`}>
                    <div className="max-w-4xl mx-auto flex justify-between items-center text-sm">
                        <div className="flex gap-2">
                            <button onClick={() => setSelectedPoster(posterA)} className="px-4 py-2 rounded-full bg-zinc-800 hover:bg-zinc-700">Poster A</button>
                            <button onClick={() => setSelectedPoster(posterB)} className="px-4 py-2 rounded-full bg-zinc-800 hover:bg-zinc-700">Poster B</button>
                        </div>
                        <AppButton onClick={handleFullscreenToggle} size="sm" variant="secondary" icon={isFullscreen ? Minimize : Maximize}>
                            {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                        </AppButton>
                    </div>
                </div>
            )}

            <HealthCheckSection allData={allData} saveFilmData={saveFilmData} setNotification={setNotification} />
            <AudienceDnaSection filmData={filmData} />
            <TalentStrategySection 
                filmData={filmData} 
                saveFilmData={saveFilmData} 
                setNotification={setNotification} 
            />
            <VisualStrategySection 
                visualAnalysis={visualAnalysis} 
                posterA={posterA} 
                posterB={posterB} 
                filmData={filmData}
                saveFilmData={saveFilmData}
                setNotification={setNotification}
                isPublicView={isPublicView}
            />
            <TrailerDnaSection 
                videoAnalysis={videoAnalysis} 
                videoKeyframes={filmData?.video_keyframes_urls} 
                filmData={filmData}
                saveFilmData={saveFilmData}
                setNotification={setNotification}
                isPublicView={isPublicView}
            />
            <PressStrategySection pressStrategy={filmData?.press_strategy} />
            <BudgetStrategySection budgetPlan={budgetPlan} formatCurrency={formatCurrency} />
            <PhasedTimelineSection budgetPlan={budgetPlan} />
            <RevenueStrategySection revenueForecast={revenueForecast} formatCurrency={formatCurrency} />
            <CampaignStrategySection campaigns={campaigns} />
            <ContactSection filmData={filmData} />
        </div>
    );
};

export default DossierTab;